// UI Language type
export type UILanguage = 'de' | 'en' | 'tr';

export interface Translations {
  // Common
  common: {
    loading: string;
    save: string;
    saved: string;
    cancel: string;
    delete: string;
    edit: string;
    add: string;
    search: string;
    back: string;
    close: string;
    confirm: string;
    yes: string;
    no: string;
    optional: string;
    error: string;
    welcome: string;
    whatsNew: string;
    updateAvailable: string;
    updateDescription: string;
    updateNow: string;
    syncRecommended: string;
    play: string;
  };
  // Navigation
  nav: {
    home: string;
    books: string;
    learn: string;
    signIn: string;
    signOut: string;
    admin: string;
    profile: string;
    support: string;
  };
  // Profile
  profile: {
    description: string;
    accountInfo: string;
    dangerZone: string;
    resetDescription: string;
    resetProgress: string;
    resetConfirmTitle: string;
    resetConfirmDescription: string;
    confirmReset: string;
    changePassword: string;
    changePasswordDesc: string;
    newPassword: string;
    confirmPassword: string;
    changePasswordButton: string;
    passwordMinLength: string;
    passwordMismatch: string;
    passwordChanged: string;
    version: string;
    buildDate: string;
    // New profile section strings
    learningSettings: string;
    learningSettingsDesc: string;
    unvocalizedDisplay: string;
    unvocalizedDisplayDesc: string;
    accountSecurity: string;
    accountSecurityDesc: string;
    restoreProgress: string;
    restoreProgressDesc: string;
    words: string;
    daysRemaining: string;
    restore: string;
    restoreConfirmTitle: string;
    restoreConfirmDesc: string;
    whatsNew: string;
    whatsNewDesc: string;
    showAll: string;
    dangerZoneDesc: string;
    backupCreated: string;
    record: string;
    today: string;
    dailyGoal: string;
    showActivityHistory: string;
    manage: string;
    upgrade: string;
    validUntil: string;
    refreshApp: string;
    refreshAppDesc: string;
    refreshAppButton: string;
    refreshAppSuccess: string;
  };
  // Auth
  auth: {
    signIn: string;
    signUp: string;
    email: string;
    password: string;
    username: string;
    usernameHint: string;
    optional: string;
    forgotPassword: string;
    noAccount: string;
    hasAccount: string;
    signInButton: string;
    signUpButton: string;
    continueWithGoogle: string;
    orContinueWithEmail: string;
    createAccount: string;
    welcomeBack: string;
    startJourney: string;
    continueJourney: string;
    resetPassword: string;
    resetPasswordDesc: string;
    sendResetLink: string;
    resetLinkSent: string;
    resetLinkSentDesc: string;
    backToLogin: string;
    confirmEmailTitle: string;
    confirmEmailDesc: string;
    linkExpiredTitle: string;
    linkExpiredDesc: string;
    tryAgain: string;
    usernameTaken: string;
    generateNewUsername: string;
    trySuggestions: string;
    usernameHelp: string;
    passwordLeaked: string;
    passwordLeakedDesc: string;
    checkingPassword: string;
  };
  // Welcome overlay
  welcome: {
    title: string;
    subtitle: string;
    yourUsername: string;
    usernameNote: string;
    tip1: string;
    tip2: string;
    cta: string;
  };
  // Lists
  lists: {
    title: string;
    subtitle: string;
    newList: string;
    createFirst: string;
    noLists: string;
    noBook: string;
    words: string;
    learned: string;
    deleteConfirm: string;
    deleteWarning: string;
    lesson: string;
    lessons: string;
    editBookCover: string;
    // List form fields
    listName: string;
    listNamePlaceholder: string;
    book: string;
    bookPlaceholder: string;
    lessonTitle: string;
    lessonTitlePlaceholder: string;
  };
  // List Detail
  listDetail: {
    searchPlaceholder: string;
    addWord: string;
    importWords: string;
    exportWords: string;
    learnButton: string;
    noWords: string;
    addFirstWord: string;
    deleteWordConfirm: string;
    deleteWordWarning: string;
    coverImageUpdated: string;
    nameUpdated: string;
    // Toast messages
    wordDeleted: string;
    allVocabDeleted: string;
    allVocabDeletedDesc: string;
    vocabRestored: string;
    vocabRestoredDesc: string;
    exportComplete: string;
    exportCompleteDesc: string;
    nothingToExport: string;
    nothingToExportDesc: string;
    sessionExpired: string;
    sessionExpiredDesc: string;
    aiCheckStarted: string;
    aiCheckStartedDesc: string;
    undo: string;
    // Sort options
    sortCreated: string;
    sortSource: string;
    sortGerman: string;
    sortEnglish: string;
    sortTurkish: string;
    // Linked items info
    linkedItemsInfo: string;
    linkedItemsInfoDesc: string;
    linkableTooltip: string;
  };
  // List Form
  listForm: {
    missingName: string;
    missingNameDesc: string;
    missingBook: string;
    missingBookDesc: string;
    lessonTitleLabel: string;
    placeholderDe: string;
    placeholderEn: string;
    placeholderTr: string;
    noBookFound: string;
  };
  // Vocabulary Form
  vocabForm: {
    addTitle: string;
    editTitle: string;
    sourceWord: string;
    targetWordDE: string;
    targetWordEN: string;
    targetWordTR: string;
    example: string;
    note: string;
    wordAdded: string;
    wordUpdated: string;
    missingFields: string;
    duplicateError: string;
    duplicateDescription: string;
    duplicateDescriptionWithList: string;
    invalidSourceError: string;
    invalidSourceDescription: string;
    autoTranslating: string;
    autoTranslateError: string;
  };
  // Learn
  learn: {
    title: string;
    subtitle: string;
    selectList: string;
    selectMode: string;
    sessionSize: string;
    sessionSizeDesc: string;
    flashcards: string;
    multipleChoice: string;
    flashcardsDesc: string;
    multipleChoiceDesc: string;
    startLearning: string;
    noLists: string;
    createListFirst: string;
    minWordsRequired: string;
    dueWords: string;
    noDueHint: string;
    selectListFirst: string;
    chooseModeFirst: string;
    wordsCount: string;
    currentlyLearning: string;
    recommendations: string;
    noCurrentLists: string;
    // Step hints
    step1SelectList: string;
    step2SelectMode: string;
    step3Ready: string;
    listSelected: string;
    allDueSelected: string;
    allDueWords: string;
    typingMode: string;
    wordPairs: string;
    dialogueMode: string;
    crossListLearning: string;
    selectLesson: string;
    chooseFromList: string;
    // Progress tooltips
    progressMastered: string;
    progressLearning: string;
    progressReviewing: string;
    progressNew: string;
    // Basics section descriptions
    lettersDescription: string;
    lettersCompletedHint: string;
    harakatDescription: string;
    harakatHint: string;
    formsDescription: string;
    formsHint: string;
    firstWordsDescription: string;
    firstWordsRecommendation: string;
    dueWordsDescription: string;
    dueWordsHint: string;
    selectLessonDescription: string;
    tanwinDescription: string;
    tanwinHint: string;
    sukunDescription: string;
    sukunHint: string;
    shaddaDescription: string;
    shaddaHint: string;
    nowActive: string;
    // Letter Introduction Modal
    letterIntro: {
      skip: string;
      back: string;
      next: string;
      startNow: string;
      // Slide 1
      slide1Title: string;
      slide1Desc: string;
      germanLabel: string;
      sourceLabel: string;
      leftToRight: string;
      rightToLeft: string;
      exampleHouse: string;
      // Slide 2
      slide2Title: string;
      slide2Desc: string;
      exampleWord: string;
      meansHouse: string;
      baseForm: string;
      atEnd: string;
      inMiddle: string;
      atStart: string;
      endForm: string;
      middleForm: string;
      initialForm: string;
      connectionTip: string;
      // Slide 3
      slide3Title: string;
      slide3Desc: string;
      youCanDoIt: string;
      whatYouLearned: string;
      learned1: string;
      learned2: string;
      learned3: string;
    };
  };
  // Flashcard Session
  flashcard: {
    tapToReveal: string;
    translation: string;
    known: string;
    uncertain: string;
    unknown: string;
    sessionComplete: string;
    correctAnswers: string;
    backToLearn: string;
    continueSession: string;
    wordsReviewed: string;
    backToList: string;
    backToDashboard: string;
    learnMore: string;
  };
  // Multiple Choice
  multipleChoice: {
    selectAnswer: string;
    correct: string;
    incorrect: string;
    next: string;
  };
  // Typing Mode
  typing: {
    typeGermanWord: string;
    check: string;
    dontKnow: string;
    correctAnswer: string;
  };
  // Orientation hints
  orientation: {
    landscapeHint: string;
  };
  // Import
  import: {
    title: string;
    pasteOrUpload: string;
    uploadFile: string;
    preview: string;
    importButton: string;
    columnMapping: string;
    source: string;
    target: string;
    newItems: string;
    duplicates: string;
    invalid: string;
  };
  // Status
  status: {
    new: string;
    learning: string;
    reviewing: string;
    mastered: string;
  };
  // Language selector
  language: {
    selectLanguage: string;
    german: string;
    english: string;
    turkish: string;
  };
  // Theme
  theme: {
    light: string;
    dark: string;
    system: string;
  };
  // Progress stats
  progress: {
    title: string;
    totalWords: string;
    mastered: string;
    learning: string;
    dueToday: string;
    streak: string;
    noProgress: string;
    startLearning: string;
    listBreakdown: string;
  };
  // Dashboard
  dashboard: {
    todayProgress: string;
    wordsToStreak: string;
    xpExplanation: string;
    longestStreak: string;
    activityHeatmap: string;
    weeklyOverview: string;
    noActivity: string;
    streakHint: string;
    bonusTitle: string;
    bonusDescription: string;
    // Weekday labels for heatmap
    weekdayMon: string;
    weekdayWed: string;
    weekdayFri: string;
  };
  // Dialogue exercise
  dialogue: {
    noGaps: string;
  };
  // Reading/Typing Card
  readingTyping: {
    notQuiteRight: string;
    perfectWithHarakat: string;
    correct: string;
    typeGermanWord: string;
    hideTranslation: string;
    showTranslation: string;
    correctAnswer: string;
  };
  // Session labels
  session: {
    sessionProgress: string;
  };
  // Status explanations for tooltips
  statusExplanations: {
    total: { label: string; description: string };
    due: { label: string; description: string };
    new: { label: string; description: string };
    learning: { label: string; description: string };
    reviewing: { label: string; description: string };
    mastered: { label: string; description: string };
  };
  // Spaced repetition explanation
  spacedRepetition: {
    title: string;
    description: string;
    howItWorks: string;
    newDesc: string;
    learningDesc: string;
    reviewingDesc: string;
    masteredDesc: string;
    intervals: string;
    intervalsDesc: string;
    dualReadingTitle: string;
    dualReadingDesc: string;
  };
  // Landing page
  landing: {
    hero: {
      tagline: string;
      textbookSeries: string;
      startLearning: string;
      createFreeAccount: string;
      scrollHint: string;
    };
    about: {
      title: string;
      description1: string;
      description2: string;
      description3: string;
    };
    howItWorks: {
      title: string;
      step1Title: string;
      step1Desc: string;
      step2Title: string;
      step2Desc: string;
      step3Title: string;
      step3Desc: string;
    };
    books: {
      title: string;
      subtitle: string;
      levelBeginner: string;
      levelElementary: string;
      levelIntermediate: string;
      levelAdvanced: string;
      vocabCount: string;
    };
    disclaimer: {
      title: string;
      text1: string;
      text2: string;
      text3: string;
      googleAuth: string;
    };
    footer: {
      tagline: string;
      rights: string;
    };
    features: {
      title: string;
      subtitle: string;
    };
  };
  // Legal pages
  legal: {
    impressum: string;
    datenschutz: string;
    backToHome: string;
    allRightsReserved: string;
    impAngaben: string;
    impKontakt: string;
    impVerantwortlich: string;
    impHaftung: string;
    impHaftungText: string;
    dsVerantwortlicher: string;
    dsDatenerfassung: string;
    dsDatenerfassungText: string;
    dsCookies: string;
    dsCookiesText: string;
    dsHosting: string;
    dsHostingText: string;
    dsRechte: string;
    dsRechteText: string;
    dsAenderungen: string;
    dsAenderungenText: string;
  };
  // Subscription
  subscription: {
    premiumRequired: string;
    freeLessonsUsed: string;
    youWantToAccess: string;
    lessonNumber: string;
    premiumBenefits: string;
    unlimitedLessons: string;
    allExercises: string;
    progressTracking: string;
    month: string;
    cancelAnytime: string;
    subscribePremium: string;
    maybeLater: string;
    manageSubscription: string;
    subscriptionActive: string;
    subscriptionCanceled: string;
    subscriptionEnds: string;
    subscriptionEndsAfterCancel: string;
    refreshStatus: string;
  };
  // Report vocabulary
  reportVocabulary: {
    title: string;
    selectReason: string;
    reasonTranslationWrong: string;
    reasonVocalizationWrong: string;
    reasonWaEnumeration: string;
    reasonGrammarWrong: string;
    reasonOther: string;
    describeProblem: string;
    notificationNote: string;
    reportButton: string;
    reportTooltip: string;
    successTitle: string;
    successDescription: string;
    errorDescription: string;
  };
  // My reports section
  myReports: {
    title: string;
    description: string;
    deleteAll: string;
    deleteAllTitle: string;
    deleteAllConfirm: string;
    deleting: string;
    typeVocabulary: string;
    typeDialogue: string;
    yourReport: string;
    adminResponse: string;
    statusPendingMessage: string;
    statusApprovedVocab: string;
    statusApprovedDialogue: string;
    statusRejectedMessage: string;
    statusPending: string;
    statusApproved: string;
    statusResolved: string;
    statusRejected: string;
    // New fields for correction feedback
    changesMade: string;
    before: string;
    after: string;
    feedback: string;
    viewVocabulary: string;
    correctedOn: string;
    noChanges: string;
  };
  // Admin area
  admin: {
    flags: {
      title: string;
      description: string;
      vocabularyFlags: string;
      dialogueFlags: string;
      pending: string;
      aiReviewed: string;
      approved: string;
      rejected: string;
      resolved: string;
      all: string;
      timestamp: string;
      role: string;
      german: string;
      translation: string;
      list: string;
      listId: string;
      reason: string;
      status: string;
      actions: string;
      approve: string;
      reject: string;
      delete: string;
      aiCheck: string;
      aiRetry: string;
      tableView: string;
      cardView: string;
      webResearch: string;
      webResearchHint: string;
      bulkRetry: string;
      bulkRetryAll: string;
      retryDialogTitle: string;
      retryDialogDesc: string;
      adminHint: string;
      noFlags: string;
      aiUsage: string;
      estimatedCost: string;
      reasonGermanWrong: string;
      reasonVocalizationWrong: string;
      reasonDuplicate: string;
      reasonTranslationWrong: string;
      reasonGrammarWrong: string;
      reasonDeleteRequest: string;
      reasonUserReport: string;
      reasonCaseWrong: string;
      reasonWaPrefix: string;
      reasonBulkReview: string;
      reasonOther: string;
      tooltipReject: string;
      tooltipDeleteVocab: string;
      tooltipApprove: string;
      tooltipRetryAI: string;
      tooltipDuplicateWarning: string;
      roleAdmin: string;
      roleTeacher: string;
      roleUser: string;
    };
  };
  // Streak celebration
  streak: {
    celebration: {
      title: string;
      daysInRow: string;
      keepGoing: string;
    };
  };
  // Contact/Support
  contact: {
    title: string;
    subtitle: string;
    formTitle: string;
    formDescription: string;
    fields: {
      name: string;
      email: string;
      category: string;
      message: string;
    };
    placeholders: {
      name: string;
      email: string;
      category: string;
      message: string;
    };
    categories: {
      technical: string;
      vocabulary: string;
      payment: string;
      feedback: string;
      other: string;
    };
    buttons: {
      submit: string;
      sending: string;
    };
    success: {
      title: string;
      description: string;
      sendAnother: string;
      backHome: string;
    };
    errors: {
      nameRequired: string;
      emailInvalid: string;
      categoryRequired: string;
      messageMinLength: string;
      submitFailed: string;
    };
    charMinimum: string;
    responseTime: string;
  };
  // Admin Support
  adminSupport: {
    title: string;
    description: string;
    filters: string;
    searchPlaceholder: string;
    filterByStatus: string;
    allRequests: string;
    requests: string;
    requestsCount: string;
    noRequests: string;
    total: string;
    date: string;
    status: string;
    actions: string;
    view: string;
    requestDetails: string;
    statusOpen: string;
    statusInProgress: string;
    statusResolved: string;
    statusUpdated: string;
    requestDeleted: string;
  };
  onboarding: {
    skipIntro: string;
    swipeHint: string;
    tapToReveal: string;
    translateThis: string;
    listenAndChoose: string;
    slide1: {
      title: string;
      subtitle: string;
    };
    slide4: {
      ready: string;
      freeHint: string;
      startButton: string;
      loginLink: string;
    };
    community: {
      title: string;
      subtitle: string;
      flagHint: string;
      fixedErrors: string;
      responseTime: string;
      thanks: string;
    };
    feedback: {
      title: string;
      subtitle: string;
      supportButton: string;
      supportDesc: string;
      recentIdeas: string;
      idea1: string;
      idea2: string;
      idea3: string;
    };
    personalized: {
      title: string;
      subtitle: string;
      question: string;
      beginner: string;
      beginnerDesc: string;
      advanced: string;
      advancedDesc: string;
      hint: string;
    };
    exercises: {
      letters: string;
      letterForms: string;
      flashcards: string;
      multipleChoice: string;
      typing: string;
      wordPairs: string;
      texts: string;
      listening: string;
      numbersIntro: string;
      numbersListen: string;
      uhrzeitIntro: string;
      uhrzeitListen: string;
    };
    exerciseDesc: {
      letters: string;
      letterForms: string;
      flashcards: string;
      multipleChoice: string;
      typing: string;
      wordPairs: string;
      texts: string;
      listening: string;
      numbersIntro: string;
      numbersListen: string;
      uhrzeitIntro: string;
      uhrzeitListen: string;
    };
    errorHints: {
      shadda: string;
      sukun: string;
      harakat: string;
      tanwin: string;
      madd: string;
    };
  };
}

export const translations: Record<UILanguage, Translations> = {
  de: {
    common: {
      loading: 'Laden...',
      save: 'Speichern',
      saved: 'Gespeichert',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      add: 'Hinzufügen',
      search: 'Suchen',
      back: 'Zurück',
      close: 'Schließen',
      confirm: 'Bestätigen',
      yes: 'Ja',
      no: 'Nein',
      optional: 'optional',
      error: 'Fehler',
      welcome: 'Willkommen',
      whatsNew: 'Neu',
      updateAvailable: 'Neue Version verfügbar',
      updateDescription: 'Eine neue Version von GermanPath ist verfügbar.',
      updateNow: 'Jetzt aktualisieren',
      syncRecommended: 'Nach dem Update empfehlen wir, deine Daten zu synchronisieren.',
      play: 'Abspielen',
    },
    nav: {
      home: 'Start',
      books: 'Bücher',
      learn: 'Lernen',
      signIn: 'Anmelden',
      signOut: 'Abmelden',
      admin: 'Admin',
      profile: 'Profil',
      support: 'Hilfe',
    },
    profile: {
      description: 'Verwalte dein Konto und deinen Lernfortschritt',
      accountInfo: 'Kontoinformationen',
      dangerZone: 'Gefahrenzone',
      resetDescription: 'Diese Aktionen können nicht rückgängig gemacht werden.',
      resetProgress: 'Lernfortschritt zurücksetzen',
      resetConfirmTitle: 'Fortschritt wirklich zurücksetzen?',
      resetConfirmDescription: 'Diese Aktion löscht deinen gesamten Lernfortschritt für alle Vokabeln. Du startest danach wieder von vorne. Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmReset: 'Ja, alles zurücksetzen',
      changePassword: 'Passwort ändern',
      changePasswordDesc: 'Gib ein neues Passwort ein, um dein aktuelles Passwort zu ändern.',
      newPassword: 'Neues Passwort',
      confirmPassword: 'Passwort bestätigen',
      changePasswordButton: 'Passwort ändern',
      passwordMinLength: 'Das Passwort muss mindestens 6 Zeichen lang sein.',
      passwordMismatch: 'Die Passwörter stimmen nicht überein.',
      passwordChanged: 'Dein Passwort wurde geändert.',
      version: 'Version',
      buildDate: 'Build-Datum',
      learningSettings: 'Lerneinstellungen',
      learningSettingsDesc: 'Passe dein Lernerlebnis an',
      unvocalizedDisplay: 'Unvokalisierte Anzeige',
      unvocalizedDisplayDesc: 'Zeigt arabische Wörter ohne Vokalzeichen',
      accountSecurity: 'Konto & Sicherheit',
      accountSecurityDesc: 'Accountdaten und Passwort verwalten',
      restoreProgress: 'Fortschritt wiederherstellen',
      restoreProgressDesc: 'Du hast {count} gesicherte Version(en) deines Lernfortschritts.',
      words: 'Wörter',
      daysRemaining: 'Tage übrig',
      restore: 'Wiederherstellen',
      restoreConfirmTitle: 'Fortschritt wiederherstellen?',
      restoreConfirmDesc: 'Dein aktueller Lernfortschritt wird durch die Version vom {date} ersetzt.',
      whatsNew: 'Was ist neu?',
      whatsNewDesc: 'Letzte Updates und Verbesserungen',
      showAll: 'Alle anzeigen',
      dangerZoneDesc: 'Setze deinen gesamten Lernfortschritt zurück. Ein Backup wird automatisch erstellt.',
      backupCreated: 'Ein Backup wird automatisch erstellt.',
      record: 'Rekord',
      today: 'Heute',
      dailyGoal: 'Tagesziel',
      showActivityHistory: 'Aktivitätsverlauf anzeigen',
      manage: 'Verwalten',
      upgrade: 'Upgrade',
      validUntil: 'bis',
      refreshApp: 'App aktualisieren',
      refreshAppDesc: 'Löscht den Cache und lädt die neueste Version.',
      refreshAppButton: 'Jetzt aktualisieren',
      refreshAppSuccess: 'App wird neu geladen...',
    },
    auth: {
      signIn: 'Anmelden',
      signUp: 'Registrieren',
      email: 'E-Mail',
      password: 'Passwort',
      username: 'Benutzername',
      usernameHint: 'Wird automatisch generiert, falls leer. Später nicht änderbar.',
      optional: 'optional',
      forgotPassword: 'Passwort vergessen?',
      noAccount: 'Noch kein Konto?',
      hasAccount: 'Bereits ein Konto?',
      signInButton: 'Anmelden',
      signUpButton: 'Registrieren',
      continueWithGoogle: 'Mit Google fortfahren',
      orContinueWithEmail: 'Oder mit E-Mail fortfahren',
      createAccount: 'Konto erstellen',
      welcomeBack: 'Willkommen zurück',
      startJourney: 'Starte deine Deutsch-Vokabel-Reise',
      continueJourney: 'Setze dein Deutsch-Lernen fort',
      resetPassword: 'Passwort zurücksetzen',
      resetPasswordDesc: 'Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.',
      sendResetLink: 'Link senden',
      resetLinkSent: 'Link gesendet!',
      resetLinkSentDesc: 'Prüfe dein E-Mail-Postfach für den Zurücksetzungs-Link.',
      backToLogin: 'Zurück zur Anmeldung',
      confirmEmailTitle: 'Bestätige deine E-Mail',
      confirmEmailDesc: 'Wir haben dir eine E-Mail mit einem Bestätigungslink gesendet. Bitte klicke auf den Link, um dein Konto zu aktivieren.',
      linkExpiredTitle: 'Link abgelaufen',
      linkExpiredDesc: 'Der Bestätigungslink ist ungültig oder abgelaufen. Bitte versuche es erneut.',
      tryAgain: 'Erneut registrieren',
      usernameTaken: 'Dieser Benutzername ist bereits vergeben',
      generateNewUsername: 'Neuen Namen generieren',
      trySuggestions: 'Vorschläge',
      usernameHelp: '3-15 Zeichen, nur Kleinbuchstaben und Zahlen',
      passwordLeaked: 'Unsicheres Passwort',
      passwordLeakedDesc: 'Dieses Passwort wurde in einem Datenleck gefunden. Bitte wähle ein anderes.',
      checkingPassword: 'Prüfe Passwort...',
    },
    welcome: {
      title: 'Herzlich willkommen!',
      subtitle: 'Schön, dass du dabei bist. Deine Reise ins Deutsche beginnt jetzt.',
      yourUsername: 'Dein Benutzername',
      usernameNote: 'Diesen Namen siehst du in deinem Profil.',
      tip1: 'Starte mit den Vokabellisten deines Buches',
      tip2: 'Lerne täglich für die besten Ergebnisse',
      cta: "Los geht's!",
    },
    lists: {
      title: 'Meine Vokabellisten',
      subtitle: 'Wähle eine Liste zum Lernen',
      newList: 'Neue Liste',
      createFirst: 'Erste Liste erstellen',
      noLists: 'Noch keine Listen vorhanden.',
      noBook: 'Andere Listen',
      words: 'Wörter',
      learned: 'gelernt',
      deleteConfirm: 'Liste löschen?',
      deleteWarning: 'Diese Aktion kann nicht rückgängig gemacht werden. Alle Vokabeln werden gelöscht.',
      lesson: 'Lektion',
      lessons: 'Lektionen',
      editBookCover: 'Buch-Cover bearbeiten',
      listName: 'Listen-ID',
      listNamePlaceholder: 'z.B. Book I-1-10',
      book: 'Buch',
      bookPlaceholder: 'z.B. ABY I-1',
      lessonTitle: 'Lektion',
      lessonTitlePlaceholder: 'z.B. 1',
    },
    listDetail: {
      searchPlaceholder: 'Vokabeln durchsuchen...',
      addWord: 'Hinzufügen',
      importWords: 'Importieren',
      exportWords: 'Exportieren',
      learnButton: 'Lernen',
      noWords: 'Noch keine Vokabeln',
      addFirstWord: 'Füge deine erste Vokabel hinzu.',
      deleteWordConfirm: 'Vokabel löschen?',
      deleteWordWarning: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      coverImageUpdated: 'Titelbild aktualisiert',
      nameUpdated: 'Name aktualisiert',
      wordDeleted: 'Vokabel gelöscht',
      allVocabDeleted: 'Alle Vokabeln gelöscht',
      allVocabDeletedDesc: '{count} Vokabeln wurden entfernt. Backup wurde heruntergeladen.',
      vocabRestored: 'Vokabeln wiederhergestellt',
      vocabRestoredDesc: '{count} Vokabeln wurden wiederhergestellt.',
      exportComplete: 'Export abgeschlossen',
      exportCompleteDesc: '{count} Vokabeln heruntergeladen.',
      nothingToExport: 'Nichts zu exportieren',
      nothingToExportDesc: 'Füge zuerst Vokabeln hinzu.',
      sessionExpired: 'Bitte erneut einloggen',
      sessionExpiredDesc: 'Deine Sitzung ist abgelaufen. Bitte melde dich neu an.',
      aiCheckStarted: 'KI-Prüfung gestartet',
      aiCheckStartedDesc: 'Die Vokabeln werden geprüft.',
      undo: 'Rückgängig',
      sortCreated: 'Erstellungsdatum',
      sortSource: 'Quellsprache (A-Z)',
      sortGerman: 'Deutsch (A-Z)',
      sortEnglish: 'Englisch (A-Z)',
      sortTurkish: 'Türkisch (A-Z)',
      linkedItemsInfo: '{count} Vokabeln sind in anderen Listen verlinkt',
      linkedItemsInfoDesc: 'Diese Vokabeln bleiben erhalten und werden automatisch einer anderen Liste zugewiesen.',
      linkableTooltip: 'Existiert bereits in einer anderen Liste und wird verlinkt statt dupliziert.',
    },
    vocabForm: {
      addTitle: 'Neue Vokabel',
      editTitle: 'Vokabel bearbeiten',
      sourceWord: 'Deutsches Wort',
      targetWordDE: 'Übersetzung (Deutsch)',
      targetWordEN: 'Übersetzung (Englisch)',
      targetWordTR: 'Übersetzung (Türkisch)',
      example: 'Beispielsatz',
      note: 'Notiz',
      wordAdded: 'Vokabel hinzugefügt',
      wordUpdated: 'Vokabel aktualisiert',
      missingFields: 'Bitte fülle alle Pflichtfelder aus.',
      duplicateError: 'Duplikat gefunden',
      duplicateDescription: 'Dieses deutsche Wort existiert bereits in der Datenbank.',
      duplicateDescriptionWithList: 'Dieses Wort existiert bereits in {listName}.',
      invalidSourceError: 'Ungültiges Wort',
      invalidSourceDescription: 'Das Quellwort muss ein gültiges deutsches Wort sein.',
      autoTranslating: 'Übersetzungen werden geladen...',
      autoTranslateError: 'Automatische Übersetzung fehlgeschlagen',
    },
    learn: {
      title: 'Lernen starten',
      subtitle: 'Wähle eine Liste und einen Lernmodus.',
      selectList: 'Liste auswählen',
      selectMode: 'Lernmodus wählen',
      sessionSize: 'Sitzungsgröße',
      sessionSizeDesc: 'Wörter pro Sitzung',
      flashcards: 'Karteikarten',
      multipleChoice: 'Multiple Choice',
      flashcardsDesc: 'Klassisches Karteikarten-Lernen',
      multipleChoiceDesc: 'Wähle die richtige Antwort',
      startLearning: 'Lernen starten',
      noLists: 'Keine Listen verfügbar',
      createListFirst: 'Wähle ein Buch aus um loszulegen.',
      minWordsRequired: 'Min. 4 Wörter erforderlich',
      dueWords: 'fällig',
      noDueHint: 'Wörter festigen sich – bald wieder üben!',
      selectListFirst: 'Wähle oben eine Lektion',
      chooseModeFirst: 'Wähle einen Modus',
      wordsCount: 'Wörter',
      currentlyLearning: 'Aktuell',
      recommendations: 'Empfehlungen',
      noCurrentLists: 'Du hast noch keine Listen gestartet',
      step1SelectList: 'Schritt 1: Wähle unten eine Liste aus',
      step2SelectMode: 'Wähle jetzt einen Lernmodus und passe ggf. die Sitzungsgröße an',
      step3Ready: 'Bereit zum Lernen!',
      listSelected: 'ausgewählt',
      allDueSelected: 'Alle fälligen Wörter ausgewählt',
      allDueWords: 'Alle fälligen Wörter',
      typingMode: 'Eingabe',
      wordPairs: 'Wortpaare',
      dialogueMode: 'Texte',
      crossListLearning: 'Listenübergreifend lernen',
      selectLesson: 'Lektion auswählen',
      chooseFromList: 'Wähle aus deinen Listen',
      progressMastered: 'gemeistert',
      progressLearning: 'in Arbeit',
      progressReviewing: 'zur Wiederholung',
      progressNew: 'neu',
      // Basis-Beschreibungen
      lettersDescription: 'Grammatik-Grundlagen lernen.',
      lettersCompletedHint: '✔️ Grundlagen abgeschlossen – jetzt zum Vokabeltraining.',
      harakatDescription: 'Grundlegende Konjugation verstehen.',
      harakatHint: 'Deutsche Verben richtig konjugieren.',
      formsDescription: 'Deklination von Artikeln und Nomen üben.',
      formsHint: 'Der, die, das – und ihre Fälle.',
      firstWordsDescription: 'Einfache Alltagswörter lesen und verstehen.',
      firstWordsRecommendation: 'Empfohlen nach den Grundlagen.',
      dueWordsDescription: 'Wörter aus allen Lektionen wiederholen.',
      dueWordsHint: 'Für später – erst die Grundlagen festigen.',
      selectLessonDescription: 'Wähle, was du üben möchtest.',
      tanwinDescription: 'Trennbare Verben verstehen.',
      tanwinHint: 'auf|stehen, an|rufen, mit|kommen...',
      sukunDescription: 'Präpositionen und ihre Fälle lernen.',
      sukunHint: 'Mit, bei, nach, zu, aus, von, seit...',
      shaddaDescription: 'Zusammengesetzte Wörter erkennen.',
      shaddaHint: 'Handschuh, Kühlschrank, Haustür...',
      nowActive: 'Jetzt aktiv',
      letterIntro: {
        skip: 'Überspringen',
        back: 'Zurück',
        next: 'Weiter',
        startNow: 'Jetzt üben!',
        slide1Title: 'Deutsch lernen leicht gemacht!',
        slide1Desc: 'Deutsch hat viele Besonderheiten, die wir Schritt für Schritt lernen.',
        germanLabel: 'Türkisch:',
        sourceLabel: 'Deutsch:',
        leftToRight: 'links → rechts',
        rightToLeft: 'links → rechts',
        exampleHouse: '💡 Das Wort "Ev" auf Deutsch:',
        slide2Title: 'Artikel verstehen',
        slide2Desc: 'Im Deutschen hat jedes Nomen einen Artikel: der, die oder das.',
        exampleWord: 'Beispielwort:',
        meansHouse: '(bedeutet "ev")',
        baseForm: 'Grundform',
        atEnd: 'Nominativ',
        inMiddle: 'Akkusativ',
        atStart: 'Dativ',
        endForm: 'Nominativ',
        middleForm: 'Akkusativ',
        initialForm: 'Dativ',
        connectionTip: '💡 Tipp: Lerne Nomen immer mit dem Artikel!',
        slide3Title: 'Du bist bereit!',
        slide3Desc: 'Jetzt weißt du, wie deutsche Wörter aufgebaut sind. Los geht\'s!',
        youCanDoIt: 'Du schaffst das! 🎉',
        whatYouLearned: 'Das hast du gelernt:',
        learned1: '✓ Deutsche Nomen haben Artikel',
        learned2: '✓ Artikel ändern sich je nach Fall',
        learned3: '✓ Es gibt drei Geschlechter',
      },
    },
    flashcard: {
      tapToReveal: 'Tippe um aufzudecken',
      translation: 'Übersetzung',
      known: 'Gewusst',
      uncertain: 'Unsicher',
      unknown: 'Nicht gewusst',
      sessionComplete: 'Geschafft!',
      correctAnswers: 'Richtige Antworten',
      backToLearn: 'Zurück zum Lernen',
      continueSession: 'Weitermachen',
      wordsReviewed: 'Du hast {count} Wörter wiederholt.',
      backToList: 'Zurück zur Liste',
      backToDashboard: 'Zurück zum Dashboard',
      learnMore: 'Weiterlernen',
    },
    multipleChoice: {
      selectAnswer: 'Wähle die richtige Antwort',
      correct: 'Richtig!',
      incorrect: 'Falsch',
      next: 'Weiter',
    },
    typing: {
      typeGermanWord: 'Gib das deutsche Wort ein',
      check: 'Prüfen',
      dontKnow: 'Weiß nicht',
      correctAnswer: 'Richtige Antwort',
    },
    orientation: {
      landscapeHint: '💡 Tipp: Drehe dein Gerät hochkant für bessere Übersicht',
    },
    import: {
      title: 'Vokabeln importieren',
      pasteOrUpload: 'Füge Text ein oder lade eine Datei hoch',
      uploadFile: 'Datei hochladen',
      preview: 'Vorschau',
      importButton: 'Importieren',
      columnMapping: 'Spaltenzuordnung',
      source: 'Quelle',
      target: 'Übersetzung',
      newItems: 'Neu',
      duplicates: 'Duplikate',
      invalid: 'Ungültig',
    },
    status: {
      new: 'Neu',
      learning: 'Lernend',
      reviewing: 'Wiederholung',
      mastered: 'Gemeistert',
    },
    language: {
      selectLanguage: 'Sprache',
      german: 'Deutsch',
      english: 'English',
      turkish: 'Türkçe',
    },
    landing: {
      hero: {
        tagline: 'Ein strukturierter Weg zum Deutsch-Vokabular.',
        textbookSeries: '(Deutschkurse für Türkischsprachige)',
        startLearning: 'Lernen starten',
        createFreeAccount: 'Kostenloses Konto erstellen',
        scrollHint: 'Mehr erfahren',
      },
      about: {
        title: 'Warum GermanPath?',
        description1: 'GermanPath hilft dir, systematisch deutsches Vokabular aufzubauen – für Integrationskurse, Sprachzertifikate oder deinen persönlichen Wortschatz.',
        description2: 'Unsere Vokabellisten sind speziell auf die Bedürfnisse türkischsprachiger Deutschlerner abgestimmt.',
        description3: 'Mit Spaced Repetition lernst du effektiver und behältst Vokabeln langfristig.',
      },
      howItWorks: {
        title: 'So funktioniert es',
        step1Title: 'Wähle ein Buch',
        step1Desc: 'Wähle aus der strukturierten Reihe von Deutschkursen.',
        step2Title: 'Lerne Deutsch-Vokabeln',
        step2Desc: 'Lerne Wörter im Kontext mit Beispielen und Notizen.',
        step3Title: 'Wiederhole mit Spaced Repetition',
        step3Desc: 'Behalte Vokabeln durch wissenschaftlich bewährte Intervalle.',
      },
      books: {
        title: 'Verfügbare Kurse',
        subtitle: 'Vorschau verfügbar. Persönlicher Fortschritt wird nach der Anmeldung gespeichert.',
        levelBeginner: 'Anfänger (A1)',
        levelElementary: 'Grundstufe (A2)',
        levelIntermediate: 'Mittelstufe (B1)',
        levelAdvanced: 'Fortgeschritten (B2+)',
        vocabCount: 'Vokabeln',
      },
      disclaimer: {
        title: 'Rechtlicher Hinweis:',
        text1: 'GermanPath ist eine unabhängige Lernplattform zur Unterstützung des Deutschlernens.',
        text2: 'Die Plattform ist nicht mit offiziellen Sprachinstituten verbunden. Alle Marken und Namen sind Eigentum ihrer jeweiligen Inhaber.',
        text3: 'GermanPath stellt keine offiziellen Kursmaterialien zur Verfügung. Externe Links dienen nur zu Informationszwecken.',
        googleAuth: 'Diese Anwendung nutzt Google-Authentifizierung für die Anmeldung. Durch die Nutzung von "Mit Google fortfahren" stimmst du der Verarbeitung deiner grundlegenden Profilinformationen (Name, E-Mail) für die Kontoerstellung zu.',
      },
      footer: {
        tagline: 'Ein strukturierter Weg zum Deutsch-Vokabular',
        rights: 'Alle Rechte vorbehalten.',
      },
      features: {
        title: 'Vielfältige Übungstypen',
        subtitle: 'Entdecke verschiedene Lernmethoden, die dir helfen, Deutsch effektiv zu lernen',
      },
    },
    theme: {
      light: 'Hell',
      dark: 'Dunkel',
      system: 'System',
    },
    dashboard: {
      todayProgress: 'Heute',
      wordsToStreak: 'Noch {count} Wörter für Streak',
      xpExplanation: '+10 XP richtig, +5 XP unsicher, +2 XP falsch',
      longestStreak: 'Längster Streak',
      activityHeatmap: 'Aktivitätsverlauf',
      weeklyOverview: 'Diese Woche',
      noActivity: 'Keine Aktivität',
      streakHint: 'Mind. 15 Wörter für Streak',
      bonusTitle: '+20 XP Bonus!',
      bonusDescription: '30 Wörter heute - Fleißbonus!',
      weekdayMon: 'Mo',
      weekdayWed: 'Mi',
      weekdayFri: 'Fr',
    },
    dialogue: {
      noGaps: 'Dieser Dialog hat keine Lücken zum Üben.',
    },
    readingTyping: {
      notQuiteRight: 'Nicht ganz richtig',
      perfectWithHarakat: 'Perfekt!',
      correct: 'Richtig!',
      typeGermanWord: 'Tippe das deutsche Wort:',
      hideTranslation: 'Übersetzung ausblenden',
      showTranslation: 'Übersetzung einblenden',
      correctAnswer: 'Richtige Antwort:',
    },
    session: {
      sessionProgress: 'Session {current}/{total}',
    },
    listForm: {
      missingName: 'Name fehlt',
      missingNameDesc: 'Bitte gib einen Listennamen ein.',
      missingBook: 'Buch fehlt',
      missingBookDesc: 'Bitte wähle oder gib ein Buch an.',
      lessonTitleLabel: 'Lektionstitel',
      placeholderDe: 'z.B. Kennenlernen',
      placeholderEn: 'e.g. Getting to know',
      placeholderTr: 'örn. Tanışma',
      noBookFound: 'Kein Buch gefunden',
    },
    progress: {
      title: 'Dein Fortschritt',
      totalWords: 'Wörter gesamt',
      mastered: 'Gemeistert',
      learning: 'Am Lernen',
      dueToday: 'Heute fällig',
      streak: 'Tage in Folge',
      noProgress: 'Starte mit dem Lernen um deinen Fortschritt zu sehen.',
      startLearning: 'Jetzt starten',
      listBreakdown: 'Aufschlüsselung nach Lektionen',
    },
    statusExplanations: {
      total: { label: 'Gesamt', description: 'Gesamtzahl der Wörter in dieser Liste' },
      due: { label: 'Fällig', description: 'Wörter, deren Wiederholungsdatum erreicht ist und die heute gelernt werden sollten' },
      new: { label: 'Neu', description: 'Noch nie geübte Wörter' },
      learning: { label: 'Lernend', description: 'Wörter, die 1x richtig beantwortet wurden (nächste Wiederholung in 3 Tagen)' },
      reviewing: { label: 'Wiederholung', description: 'Wörter, die 2-3x richtig beantwortet wurden (Wiederholung in 7-14 Tagen)' },
      mastered: { label: 'Gemeistert', description: 'Wörter, die 4+ Mal richtig beantwortet wurden (Wiederholung in 30+ Tagen)' },
    },
    spacedRepetition: {
      title: 'Wie funktioniert das Lernsystem?',
      description: 'Wir nutzen die wissenschaftlich bewährte Spaced-Repetition-Methode.',
      howItWorks: 'Je öfter du ein Wort richtig beantwortest, desto seltener wird es abgefragt:',
      newDesc: 'Neu: Noch nie geübt',
      learningDesc: 'Lernend: 1x richtig → Wiederholung in 3 Tagen',
      reviewingDesc: 'Wiederholung: 2-3x richtig → 7-14 Tage Pause',
      masteredDesc: 'Gemeistert: 4+ Mal richtig → 30+ Tage Pause',
      intervals: 'Wiederholungsintervalle',
      intervalsDesc: 'Falsche Antworten setzen den Fortschritt zurück. "Unsicher" reduziert die Streak um 1.',
      dualReadingTitle: 'Effektives Lernen',
      dualReadingDesc: 'Jedes Wort wird mit Kontext und Beispielen angezeigt. So lernst du Deutsch natürlich und praxisnah.',
    },
    legal: {
      impressum: 'Impressum',
      datenschutz: 'Datenschutz',
      backToHome: 'Zurück zur Startseite',
      allRightsReserved: 'Alle Rechte vorbehalten.',
      impAngaben: 'Angaben gemäß § 5 TMG',
      impKontakt: 'Kontakt',
      impVerantwortlich: 'Verantwortlich für den Inhalt',
      impHaftung: 'Haftungsausschluss',
      impHaftungText: 'Die Inhalte dieser Website wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.',
      dsVerantwortlicher: 'Verantwortlicher',
      dsDatenerfassung: 'Datenerfassung auf dieser Website',
      dsDatenerfassungText: 'Wir erfassen und speichern nur die Daten, die für die Bereitstellung unserer Dienste erforderlich sind. Dazu gehören E-Mail-Adresse und Benutzername bei der Registrierung sowie Lernfortschrittsdaten.',
      dsCookies: 'Cookies',
      dsCookiesText: 'Diese Website verwendet technisch notwendige Cookies für die Authentifizierung und Session-Verwaltung. Es werden keine Tracking-Cookies verwendet.',
      dsHosting: 'Hosting',
      dsHostingText: 'Diese Website wird bei Lovable/Supabase gehostet. Die Server befinden sich in der EU.',
      dsRechte: 'Ihre Rechte',
      dsRechteText: 'Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung Ihrer personenbezogenen Daten. Kontaktieren Sie uns hierzu per E-Mail.',
      dsAenderungen: 'Änderungen',
      dsAenderungenText: 'Wir behalten uns vor, diese Datenschutzerklärung zu aktualisieren, um sie an geänderte Rechtslagen oder Änderungen unserer Dienste anzupassen.',
    },
    subscription: {
      premiumRequired: 'Premium-Zugang erforderlich',
      freeLessonsUsed: 'Du hast die ersten {count} kostenlosen Lektionen abgeschlossen.',
      youWantToAccess: 'Du möchtest zugreifen auf:',
      lessonNumber: 'Lektion {number}',
      premiumBenefits: 'Premium-Vorteile',
      unlimitedLessons: 'Unbegrenzter Zugang zu allen Lektionen',
      allExercises: 'Alle Übungsarten verfügbar',
      progressTracking: 'Lernfortschritt & Statistiken',
      month: 'Monat',
      cancelAnytime: 'Jederzeit kündbar',
      subscribePremium: 'Premium abonnieren',
      maybeLater: 'Vielleicht später',
      manageSubscription: 'Abo verwalten',
      subscriptionActive: 'Premium aktiv',
      subscriptionCanceled: 'Premium gekündigt',
      subscriptionEnds: 'Gültig bis {date}',
      subscriptionEndsAfterCancel: 'Noch aktiv bis {date}',
      refreshStatus: 'Status aktualisieren',
    },
    admin: {
      flags: {
        title: 'Gemeldete Einträge',
        description: 'Überprüfe und bearbeite gemeldete Vokabeln und Texte',
        vocabularyFlags: 'Vokabel-Meldungen',
        dialogueFlags: 'Text-Meldungen',
        pending: 'Ausstehend',
        aiReviewed: 'KI geprüft',
        approved: 'Genehmigt',
        rejected: 'Abgelehnt',
        resolved: 'Erledigt',
        all: 'Alle',
        timestamp: 'Zeitpunkt',
        role: 'Rolle',
        german: 'Deutsch',
        translation: 'Übersetzung',
        list: 'Liste',
        listId: 'Listen-ID',
        reason: 'Grund',
        status: 'Status',
        actions: 'Aktionen',
        approve: 'Genehmigen',
        reject: 'Ablehnen',
        delete: 'Löschen',
        aiCheck: 'KI prüfen',
        aiRetry: 'KI erneut',
        tableView: 'Tabelle',
        cardView: 'Karten',
        webResearch: 'Wörterbuch-Recherche',
        webResearchHint: 'Aktiviert Wiktionary-Suche für genauere Ergebnisse',
        bulkRetry: 'Alle ausstehenden prüfen',
        bulkRetryAll: 'Alle {count} prüfen',
        retryDialogTitle: 'KI-Prüfung mit Hinweis',
        retryDialogDesc: 'Optional kannst du einen Hinweis für die KI eingeben.',
        adminHint: 'Admin-Hinweis (optional)',
        noFlags: 'Keine Meldungen vorhanden',
        aiUsage: 'KI-Nutzung',
        estimatedCost: 'Geschätzte Kosten',
        reasonGermanWrong: 'Deutsch falsch',
        reasonVocalizationWrong: 'Vokalisierung falsch',
        reasonDuplicate: 'Duplikat',
        reasonTranslationWrong: 'Übersetzung falsch',
        reasonGrammarWrong: 'Grammatikfehler',
        reasonDeleteRequest: 'Löschen angefragt',
        reasonUserReport: 'Benutzer-Meldung',
        reasonCaseWrong: 'Falsche Kasusform',
        reasonWaPrefix: 'Hat وَ-Präfix',
        reasonBulkReview: 'Bulk-Prüfung',
        reasonOther: 'Sonstiges',
        tooltipReject: 'Ablehnen',
        tooltipDeleteVocab: 'Vokabel löschen',
        tooltipApprove: 'Genehmigen',
        tooltipRetryAI: 'KI prüfen',
        tooltipDuplicateWarning: 'Duplikat: Nach der Korrektur würde das Wort {word} mit einem anderen Eintrag in {book} übereinstimmen',
        roleAdmin: 'Admin',
        roleTeacher: 'Lehrer',
        roleUser: 'Benutzer',
      },
    },
    reportVocabulary: {
      title: 'Problem melden',
      selectReason: 'Wähle den Grund für deine Meldung.',
      reasonTranslationWrong: 'Falsche Übersetzung',
      reasonVocalizationWrong: 'Fehlende/Falsche Vokalisierung',
      reasonWaEnumeration: 'وَ-Aufzählung',
      reasonGrammarWrong: 'Grammatikfehler',
      reasonOther: 'Sonstiges',
      describeProblem: 'Beschreibe das Problem...',
      notificationNote: 'Nach der Prüfung wirst du über das Ergebnis informiert.',
      reportButton: 'Melden',
      reportTooltip: 'Vokabel melden',
      successTitle: 'Gemeldet',
      successDescription: 'Danke für deine Meldung! Wir prüfen die Vokabel und informieren dich über das Ergebnis.',
      errorDescription: 'Die Meldung konnte nicht gespeichert werden.',
    },
    myReports: {
      title: 'Meine Meldungen',
      description: 'Hier siehst du den Status deiner gemeldeten Vokabeln und Texte.',
      deleteAll: 'Alle löschen',
      deleteAllTitle: 'Alle Meldungen löschen?',
      deleteAllConfirm: 'Möchtest du wirklich alle {count} Meldungen löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleting: 'Löschen...',
      typeVocabulary: 'Vokabel',
      typeDialogue: 'Text',
      yourReport: 'Deine Meldung:',
      adminResponse: 'Antwort:',
      statusPendingMessage: 'Deine Meldung wird geprüft. Du erhältst eine Benachrichtigung, sobald sie bearbeitet wurde.',
      statusApprovedVocab: 'Die Vokabel wurde angepasst. Vielen Dank für deine Mithilfe!',
      statusApprovedDialogue: 'Der Text wurde angepasst. Vielen Dank für deine Mithilfe!',
      statusRejectedMessage: 'Die Meldung wurde geprüft, aber es wurde kein Fehler gefunden.',
      statusPending: 'In Prüfung',
      statusApproved: 'Angenommen',
      statusResolved: 'Korrigiert',
      statusRejected: 'Abgelehnt',
      changesMade: 'Änderungen',
      before: 'Vorher',
      after: 'Nachher',
      feedback: 'Antwort',
      viewVocabulary: 'Vokabel anzeigen',
      correctedOn: 'Korrigiert am',
      noChanges: 'Keine Änderungen vorgenommen',
    },
    streak: {
      celebration: {
        title: 'Streak erreicht!',
        daysInRow: 'Tage in Folge!',
        keepGoing: 'Weiter so!',
      },
    },
    contact: {
      title: 'Kontakt & Support',
      subtitle: 'Wir helfen dir gerne bei Fragen oder Problemen.',
      formTitle: 'Nachricht senden',
      formDescription: 'Beschreibe dein Anliegen und wir melden uns so schnell wie möglich.',
      fields: {
        name: 'Name',
        email: 'E-Mail',
        category: 'Kategorie',
        message: 'Nachricht',
      },
      placeholders: {
        name: 'Dein Name',
        email: 'deine@email.de',
        category: 'Wähle eine Kategorie',
        message: 'Beschreibe dein Anliegen hier...',
      },
      categories: {
        technical: 'Technisches Problem',
        vocabulary: 'Frage zu Vokabeln',
        payment: 'Zahlung & Abo',
        feedback: 'Feedback & Vorschläge',
        other: 'Sonstiges',
      },
      buttons: {
        submit: 'Nachricht senden',
        sending: 'Wird gesendet...',
      },
      success: {
        title: 'Nachricht gesendet!',
        description: 'Vielen Dank für deine Nachricht. Wir melden uns schnellstmöglich bei dir.',
        sendAnother: 'Weitere Nachricht senden',
        backHome: 'Zurück zur Startseite',
      },
      errors: {
        nameRequired: 'Bitte gib deinen Namen ein.',
        emailInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
        categoryRequired: 'Bitte wähle eine Kategorie.',
        messageMinLength: 'Die Nachricht muss mindestens 20 Zeichen lang sein.',
        submitFailed: 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.',
      },
      charMinimum: 'Zeichen minimum',
      responseTime: 'Wir antworten in der Regel innerhalb von 24-48 Stunden.',
    },
    adminSupport: {
      title: 'Support-Anfragen',
      description: 'Alle eingegangenen Support-Anfragen verwalten',
      filters: 'Filter',
      searchPlaceholder: 'Nach Name, E-Mail oder Nachricht suchen...',
      filterByStatus: 'Nach Status filtern',
      allRequests: 'Alle Anfragen',
      requests: 'Anfragen',
      requestsCount: 'Anfragen gefunden',
      noRequests: 'Keine Anfragen vorhanden',
      total: 'Gesamt',
      date: 'Datum',
      status: 'Status',
      actions: 'Aktionen',
      view: 'Ansehen',
      requestDetails: 'Anfrage-Details',
      statusOpen: 'Offen',
      statusInProgress: 'In Bearbeitung',
      statusResolved: 'Erledigt',
      statusUpdated: 'Status wurde aktualisiert',
      requestDeleted: 'Anfrage wurde gelöscht',
    },
    onboarding: {
      skipIntro: 'Überspringen',
      swipeHint: 'Wische zum Navigieren',
      tapToReveal: 'Tippen zum Aufdecken',
      translateThis: 'Übersetze:',
      listenAndChoose: 'Höre und wähle die richtige Antwort',
      slide1: {
        title: 'Authentisches Deutsch lernen.',
        subtitle: 'Verschiedene Übungstypen je nach Lektion.',
      },
      slide4: {
        ready: 'Bereit zum Lernen?',
        freeHint: '1-2 Lektionen unbegrenzt. Keine Anmeldung nötig.',
        startButton: 'Kostenlos starten',
        loginLink: 'Bereits Mitglied? Anmelden',
      },
      community: {
        title: 'Mit der Community',
        subtitle: 'Gemeinsam machen wir die App besser.',
        flagHint: 'Fehler melden',
        fixedErrors: 'Fehler behoben',
        responseTime: 'Reaktionszeit',
        thanks: 'Danke an alle, die mithelfen!',
      },
      feedback: {
        title: 'Deine Ideen zählen',
        subtitle: 'Teile Feedback – wir setzen es um.',
        supportButton: 'Hilfe & Feedback',
        supportDesc: 'Fragen, Wünsche, Verbesserungen',
        recentIdeas: 'Umgesetzte Nutzer-Ideen:',
        idea1: 'Mehr Hörübungen',
        idea2: 'Wortpaare-Spiel',
        idea3: 'Offline-Modus',
      },
      personalized: {
        title: 'Dein Start',
        subtitle: 'Wir passen den Lernpfad an dich an.',
        question: 'Wie gut sind deine Deutschkenntnisse?',
        beginner: 'Komplett neu',
        beginnerDesc: 'Ich starte bei den Grundlagen.',
        advanced: 'Grundlagen bekannt',
        advancedDesc: 'Ich kann etwas lesen, brauche Vokabeln.',
        hint: 'Du kannst das später ändern',
      },
      exercises: {
        letters: 'Grammatik',
        letterForms: 'Artikel',
        flashcards: 'Karteikarten',
        multipleChoice: 'Multiple Choice',
        typing: 'Eingabe',
        wordPairs: 'Wortpaare',
        texts: 'Texte',
        listening: 'Hören',
        numbersIntro: 'Zahlen kennenlernen',
        numbersListen: 'Zahlen hören',
        uhrzeitIntro: 'Uhrzeit kennenlernen',
        uhrzeitListen: 'Uhrzeit hören',
      },
      exerciseDesc: {
        letters: 'Lerne deutsche Grammatik-Grundlagen.',
        letterForms: 'Erkenne den richtigen Artikel (der, die, das).',
        flashcards: 'Klassisches Karteikarten-Lernen mit Wiederholung.',
        multipleChoice: 'Wähle die richtige Übersetzung aus vier Optionen.',
        typing: 'Tippe deutsche Wörter mit der Tastatur.',
        wordPairs: 'Verbinde deutsche Wörter mit ihren türkischen Übersetzungen.',
        texts: 'Übe mit echten Dialogen und fülle Lücken aus.',
        listening: 'Trainiere dein Gehör für deutsche Aussprache.',
        numbersIntro: 'Lerne die deutschen Zahlen 1-10 mit Audio und Beispielen.',
        numbersListen: 'Erkenne deutsche Zahlen beim Hören – Quiz-Modus.',
        uhrzeitIntro: 'Lerne die deutschen Uhrzeiten mit analogen Uhren.',
        uhrzeitListen: 'Erkenne deutsche Uhrzeiten beim Hören – Quiz-Modus.',
      },
      errorHints: {
        shadda: 'Achte auf die Betonung.',
        sukun: 'Hier fehlt etwas.',
        harakat: 'Achte genau auf die Schreibweise.',
        tanwin: 'Höre auf die Endung.',
        madd: 'Achte auf die Länge des Vokals.',
      },
    },
  },
  en: {
    common: {
      loading: 'Loading...',
      save: 'Save',
      saved: 'Saved',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      search: 'Search',
      back: 'Back',
      close: 'Close',
      confirm: 'Confirm',
      yes: 'Yes',
      no: 'No',
      optional: 'optional',
      error: 'Error',
      welcome: 'Welcome',
      whatsNew: 'New',
      updateAvailable: 'New Version Available',
      updateDescription: 'A new version of GermanPath is available.',
      updateNow: 'Update Now',
      syncRecommended: 'After updating, we recommend syncing your data.',
      play: 'Play',
    },
    nav: {
      home: 'Home',
      books: 'Books',
      learn: 'Learn',
      signIn: 'Sign In',
      signOut: 'Sign Out',
      admin: 'Admin',
      profile: 'Profile',
      support: 'Help',
    },
    profile: {
      description: 'Manage your account and learning progress',
      accountInfo: 'Account Information',
      dangerZone: 'Danger Zone',
      resetDescription: 'These actions cannot be undone.',
      resetProgress: 'Reset Learning Progress',
      resetConfirmTitle: 'Reset progress?',
      resetConfirmDescription: 'This will delete all your learning progress for all vocabulary. You will start over from scratch. This action cannot be undone.',
      confirmReset: 'Yes, reset everything',
      changePassword: 'Change Password',
      changePasswordDesc: 'Enter a new password to change your current password.',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      changePasswordButton: 'Change Password',
      passwordMinLength: 'Password must be at least 6 characters.',
      passwordMismatch: 'Passwords do not match.',
      passwordChanged: 'Your password has been changed.',
      version: 'Version',
      buildDate: 'Build Date',
      learningSettings: 'Learning Settings',
      learningSettingsDesc: 'Customize your learning experience',
      unvocalizedDisplay: 'Unvocalized Display',
      unvocalizedDisplayDesc: 'Show German words without special formatting',
      accountSecurity: 'Account & Security',
      accountSecurityDesc: 'Manage account data and password',
      restoreProgress: 'Restore Progress',
      restoreProgressDesc: 'You have {count} saved version(s) of your learning progress.',
      words: 'words',
      daysRemaining: 'days remaining',
      restore: 'Restore',
      restoreConfirmTitle: 'Restore progress?',
      restoreConfirmDesc: 'Your current learning progress will be replaced with the version from {date}.',
      whatsNew: "What's New?",
      whatsNewDesc: 'Latest updates and improvements',
      showAll: 'Show all',
      dangerZoneDesc: 'Reset all your learning progress. A backup will be created automatically.',
      backupCreated: 'A backup will be created automatically.',
      record: 'Record',
      today: 'Today',
      dailyGoal: 'Daily Goal',
      showActivityHistory: 'Show activity history',
      manage: 'Manage',
      upgrade: 'Upgrade',
      validUntil: 'until',
      refreshApp: 'Refresh app',
      refreshAppDesc: 'Clears the cache and loads the latest version.',
      refreshAppButton: 'Refresh now',
      refreshAppSuccess: 'App is reloading...',
    },
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      email: 'Email',
      password: 'Password',
      username: 'Username',
      usernameHint: 'Auto-generated if left empty. Cannot be changed later.',
      optional: 'optional',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      signInButton: 'Sign In',
      signUpButton: 'Sign Up',
      continueWithGoogle: 'Continue with Google',
      orContinueWithEmail: 'Or continue with email',
      createAccount: 'Create Account',
      welcomeBack: 'Welcome back',
      startJourney: 'Start your German vocabulary journey',
      continueJourney: 'Continue your German learning',
      resetPassword: 'Reset Password',
      resetPasswordDesc: 'Enter your email address and we will send you a reset link.',
      sendResetLink: 'Send Reset Link',
      resetLinkSent: 'Link Sent!',
      resetLinkSentDesc: 'Check your email for the password reset link.',
      backToLogin: 'Back to Login',
      confirmEmailTitle: 'Confirm your email',
      confirmEmailDesc: 'We sent you an email with a confirmation link. Please click the link to activate your account.',
      linkExpiredTitle: 'Link expired',
      linkExpiredDesc: 'The confirmation link is invalid or has expired. Please try again.',
      tryAgain: 'Register again',
      usernameTaken: 'This username is already taken',
      generateNewUsername: 'Generate new name',
      trySuggestions: 'Suggestions',
      usernameHelp: '3-15 characters, lowercase letters and numbers only',
      passwordLeaked: 'Unsafe password',
      passwordLeakedDesc: 'This password was found in a data breach. Please choose a different one.',
      checkingPassword: 'Checking password...',
    },
    welcome: {
      title: 'Welcome!',
      subtitle: "Great to have you here. Your German journey starts now.",
      yourUsername: 'Your username',
      usernameNote: "You'll see this name in your profile.",
      tip1: 'Start with the vocabulary lists from your course',
      tip2: 'Learn daily for the best results',
      cta: "Let's go!",
    },
    lists: {
      title: 'My Vocabulary Lists',
      subtitle: 'Choose a list to start learning',
      newList: 'New List',
      createFirst: 'Create First List',
      noLists: 'No lists yet.',
      noBook: 'Other Lists',
      words: 'words',
      learned: 'learned',
      deleteConfirm: 'Delete list?',
      deleteWarning: 'This action cannot be undone. All vocabulary will be deleted.',
      lesson: 'Lesson',
      lessons: 'Lessons',
      editBookCover: 'Edit Book Cover',
      listName: 'List ID',
      listNamePlaceholder: 'e.g. Book I-1-10',
      book: 'Book',
      bookPlaceholder: 'e.g. ABY I-1',
      lessonTitle: 'Lesson',
      lessonTitlePlaceholder: 'e.g. 1',
    },
    listDetail: {
      searchPlaceholder: 'Search vocabulary...',
      addWord: 'Add',
      importWords: 'Import',
      exportWords: 'Export',
      learnButton: 'Learn',
      noWords: 'No vocabulary yet',
      addFirstWord: 'Add your first vocabulary item.',
      deleteWordConfirm: 'Delete word?',
      deleteWordWarning: 'This action cannot be undone.',
      coverImageUpdated: 'Cover image updated',
      nameUpdated: 'Name updated',
      wordDeleted: 'Word deleted',
      allVocabDeleted: 'All vocabulary deleted',
      allVocabDeletedDesc: '{count} vocabulary items removed. Backup downloaded.',
      vocabRestored: 'Vocabulary restored',
      vocabRestoredDesc: '{count} vocabulary items restored.',
      exportComplete: 'Export complete',
      exportCompleteDesc: '{count} vocabulary items downloaded.',
      nothingToExport: 'Nothing to export',
      nothingToExportDesc: 'Add some vocabulary first.',
      sessionExpired: 'Please log in again',
      sessionExpiredDesc: 'Your session has expired. Please sign in again.',
      aiCheckStarted: 'AI check started',
      aiCheckStartedDesc: 'Vocabulary is being checked.',
      undo: 'Undo',
      sortCreated: 'Date created',
      sortSource: 'Source (A-Z)',
      sortGerman: 'German (A-Z)',
      sortEnglish: 'English (A-Z)',
      sortTurkish: 'Turkish (A-Z)',
      linkedItemsInfo: '{count} words are linked to other lists',
      linkedItemsInfoDesc: 'These words will be preserved and automatically assigned to another list.',
      linkableTooltip: 'Already exists in another list and will be linked instead of duplicated.',
    },
    vocabForm: {
      addTitle: 'Add New Word',
      editTitle: 'Edit Word',
      sourceWord: 'Source word',
      targetWordDE: 'Translation (German)',
      targetWordEN: 'Translation (English)',
      targetWordTR: 'Translation (Turkish)',
      example: 'Example sentence',
      note: 'Note',
      wordAdded: 'Word added',
      wordUpdated: 'Word updated',
      missingFields: 'Please fill in all required fields.',
      duplicateError: 'Duplicate found',
      duplicateDescription: 'This German word already exists in the database.',
      duplicateDescriptionWithList: 'This word already exists in {listName}.',
      invalidSourceError: 'Invalid German word',
      invalidSourceDescription: 'The source word must be a valid German word.',
      autoTranslating: 'Loading translations...',
      autoTranslateError: 'Automatic translation failed',
    },
    learn: {
      title: 'Start Learning',
      subtitle: 'Choose a list and learning mode.',
      selectList: 'Select list',
      selectMode: 'Choose mode',
      sessionSize: 'Session size',
      sessionSizeDesc: 'Words per session',
      flashcards: 'Flashcards',
      multipleChoice: 'Multiple Choice',
      flashcardsDesc: 'Classic flashcard learning',
      multipleChoiceDesc: 'Choose the correct answer',
      startLearning: 'Start Learning',
      noLists: 'No lists available',
      createListFirst: 'Choose a book to get started.',
      minWordsRequired: 'Min. 4 words required',
      dueWords: 'due',
      noDueHint: 'Words are consolidating – practice again soon!',
      selectListFirst: 'Select a lesson above',
      chooseModeFirst: 'Choose a mode',
      wordsCount: 'words',
      currentlyLearning: 'Currently Learning',
      recommendations: 'Recommendations',
      noCurrentLists: 'You haven\'t started any lists yet',
      step1SelectList: 'Step 1: Select a list below',
      step2SelectMode: 'Now choose a learning mode and adjust session size if needed',
      step3Ready: 'Ready to learn!',
      listSelected: 'selected',
      allDueSelected: 'All due words selected',
      allDueWords: 'All due words',
      typingMode: 'Typing',
      wordPairs: 'Word Pairs',
      dialogueMode: 'Texts',
      crossListLearning: 'Learn across lists',
      selectLesson: 'Select lesson',
      chooseFromList: 'Choose from your lists',
      progressMastered: 'mastered',
      progressLearning: 'in progress',
      progressReviewing: 'reviewing',
      progressNew: 'new',
      // Basics descriptions
      lettersDescription: 'Learn German grammar basics.',
      lettersCompletedHint: '✔️ Basics completed – now onto vocabulary training.',
      harakatDescription: 'Understand basic conjugation.',
      harakatHint: 'Conjugate German verbs correctly.',
      formsDescription: 'Practice declension of articles and nouns.',
      formsHint: 'Der, die, das – and their cases.',
      firstWordsDescription: 'Read and understand simple everyday words.',
      firstWordsRecommendation: 'Recommended after the basics.',
      dueWordsDescription: 'Review words from all lessons.',
      dueWordsHint: 'For later – first strengthen the basics.',
      selectLessonDescription: 'Choose what you want to practice.',
      tanwinDescription: 'Understand separable verbs.',
      tanwinHint: 'auf|stehen, an|rufen, mit|kommen...',
      sukunDescription: 'Learn prepositions and their cases.',
      sukunHint: 'Mit, bei, nach, zu, aus, von, seit...',
      shaddaDescription: 'Recognize compound words.',
      shaddaHint: 'Handschuh, Kühlschrank, Haustür...',
      nowActive: 'Now active',
      letterIntro: {
        skip: 'Skip',
        back: 'Back',
        next: 'Next',
        startNow: 'Start now!',
        slide1Title: 'Learning German made easy!',
        slide1Desc: 'German has many special features that we will learn step by step.',
        germanLabel: 'Turkish:',
        sourceLabel: 'German:',
        leftToRight: 'left → right',
        rightToLeft: 'left → right',
        exampleHouse: '💡 The word "Ev" in German:',
        slide2Title: 'Understanding articles',
        slide2Desc: 'In German, every noun has an article: der, die, or das.',
        exampleWord: 'Example word:',
        meansHouse: '(means "ev")',
        baseForm: 'Base form',
        atEnd: 'Nominative',
        inMiddle: 'Accusative',
        atStart: 'Dative',
        endForm: 'Nominative',
        middleForm: 'Accusative',
        initialForm: 'Dative',
        connectionTip: '💡 Tip: Always learn nouns with their article!',
        slide3Title: 'You are ready!',
        slide3Desc: 'Now you know how German words are structured. Let\'s go!',
        youCanDoIt: 'You can do it! 🎉',
        whatYouLearned: 'What you learned:',
        learned1: '✓ German nouns have articles',
        learned2: '✓ Articles change depending on the case',
        learned3: '✓ There are three genders',
      },
    },
    flashcard: {
      tapToReveal: 'Tap to reveal',
      translation: 'Translation',
      known: 'Known',
      uncertain: 'Uncertain',
      unknown: 'Unknown',
      sessionComplete: 'Well done!',
      correctAnswers: 'Correct answers',
      backToLearn: 'Back to Learn',
      continueSession: 'Continue',
      wordsReviewed: 'You reviewed {count} words.',
      backToList: 'Back to List',
      backToDashboard: 'Back to Dashboard',
      learnMore: 'Learn More',
    },
    multipleChoice: {
      selectAnswer: 'Select the correct answer',
      correct: 'Correct!',
      incorrect: 'Incorrect',
      next: 'Next',
    },
    typing: {
      typeGermanWord: 'Type the German word',
      check: 'Check',
      dontKnow: "Don't know",
      correctAnswer: 'Correct answer',
    },
    orientation: {
      landscapeHint: '💡 Tip: Rotate your device to portrait mode for a better overview',
    },
    import: {
      title: 'Import Vocabulary',
      pasteOrUpload: 'Paste text or upload a file',
      uploadFile: 'Upload file',
      preview: 'Preview',
      importButton: 'Import',
      columnMapping: 'Column mapping',
      source: 'Source',
      target: 'Translation',
      newItems: 'New',
      duplicates: 'Duplicates',
      invalid: 'Invalid',
    },
    status: {
      new: 'New',
      learning: 'Learning',
      reviewing: 'Reviewing',
      mastered: 'Mastered',
    },
    language: {
      selectLanguage: 'Language',
      german: 'Deutsch',
      english: 'English',
      turkish: 'Türkçe',
    },
    landing: {
      hero: {
        tagline: 'A structured path to German vocabulary.',
        textbookSeries: '(German courses for Turkish speakers)',
        startLearning: 'Start learning',
        createFreeAccount: 'Create free account',
        scrollHint: 'Learn more',
      },
      about: {
        title: 'Why GermanPath?',
        description1: 'GermanPath helps you build German vocabulary systematically – for integration courses, language certificates, or your personal word list.',
        description2: 'Our vocabulary lists are specially tailored to the needs of Turkish-speaking German learners.',
        description3: 'With spaced repetition, you learn more effectively and retain vocabulary long-term.',
      },
      howItWorks: {
        title: 'How It Works',
        step1Title: 'Choose a course',
        step1Desc: 'Select from the structured series of German courses.',
        step2Title: 'Learn German vocabulary',
        step2Desc: 'Study words in context with examples and notes.',
        step3Title: 'Review with spaced repetition',
        step3Desc: 'Retain vocabulary through scientifically-proven intervals.',
      },
      books: {
        title: 'Available Courses',
        subtitle: 'Preview available. Personal progress is saved after sign-up.',
        levelBeginner: 'Beginner (A1)',
        levelElementary: 'Elementary (A2)',
        levelIntermediate: 'Intermediate (B1)',
        levelAdvanced: 'Advanced (B2+)',
        vocabCount: 'Words',
      },
      disclaimer: {
        title: 'Legal Disclaimer:',
        text1: 'GermanPath is an independent learning platform to support German learning.',
        text2: 'The platform is not affiliated with official language institutes. All trademarks and names are property of their respective owners.',
        text3: 'GermanPath does not provide official course materials. External links are provided for informational purposes only.',
        googleAuth: 'This application uses Google authentication for sign-in. By using "Continue with Google", you agree to the processing of your basic profile information (name, email) for account creation.',
      },
      footer: {
        tagline: 'A structured path to German vocabulary',
        rights: 'All rights reserved.',
      },
      features: {
        title: 'Diverse Exercise Types',
        subtitle: 'Discover various learning methods that help you learn German effectively',
      },
    },
    theme: {
      light: 'Light',
      dark: 'Dark',
      system: 'System',
    },
    dashboard: {
      todayProgress: 'Today',
      wordsToStreak: '{count} more words for streak',
      xpExplanation: '+10 XP correct, +5 XP uncertain, +2 XP wrong',
      longestStreak: 'Longest streak',
      activityHeatmap: 'Activity history',
      weeklyOverview: 'This week',
      noActivity: 'No activity',
      streakHint: 'Min. 15 words for streak',
      bonusTitle: '+20 XP Bonus!',
      bonusDescription: '30 words today - Diligence bonus!',
      weekdayMon: 'Mon',
      weekdayWed: 'Wed',
      weekdayFri: 'Fri',
    },
    dialogue: {
      noGaps: 'This dialogue has no gaps to practice.',
    },
    readingTyping: {
      notQuiteRight: 'Not quite right',
      perfectWithHarakat: 'Perfect!',
      correct: 'Correct!',
      typeGermanWord: 'Type the German word:',
      hideTranslation: 'Hide translation',
      showTranslation: 'Show translation',
      correctAnswer: 'Correct answer:',
    },
    session: {
      sessionProgress: 'Session {current}/{total}',
    },
    listForm: {
      missingName: 'Name missing',
      missingNameDesc: 'Please enter a list name.',
      missingBook: 'Book missing',
      missingBookDesc: 'Please select or enter a book.',
      lessonTitleLabel: 'Lesson title',
      placeholderDe: 'e.g. Getting to know',
      placeholderEn: 'e.g. Getting to know',
      placeholderTr: 'e.g. Getting to know',
      noBookFound: 'No book found',
    },
    progress: {
      title: 'Your Progress',
      totalWords: 'Total words',
      mastered: 'Mastered',
      learning: 'Learning',
      dueToday: 'Due today',
      streak: 'Day streak',
      noProgress: 'Start learning to see your progress.',
      startLearning: 'Start now',
      listBreakdown: 'Breakdown by lessons',
    },
    statusExplanations: {
      total: { label: 'Total', description: 'Total number of words in this list' },
      due: { label: 'Due', description: 'Words whose review date has arrived and should be practiced today' },
      new: { label: 'New', description: 'Words that have never been practiced' },
      learning: { label: 'Learning', description: 'Words answered correctly 1 time (next review in 3 days)' },
      reviewing: { label: 'Reviewing', description: 'Words answered correctly 2-3 times (review in 7-14 days)' },
      mastered: { label: 'Mastered', description: 'Words answered correctly 4+ times (review in 30+ days)' },
    },
    spacedRepetition: {
      title: 'How does the learning system work?',
      description: 'We use the scientifically proven spaced repetition method.',
      howItWorks: 'The more often you answer a word correctly, the less frequently it will be asked:',
      newDesc: 'New: Never practiced',
      learningDesc: 'Learning: 1x correct → Review in 3 days',
      reviewingDesc: 'Reviewing: 2-3x correct → 7-14 day break',
      masteredDesc: 'Mastered: 4+ times correct → 30+ day break',
      intervals: 'Review intervals',
      intervalsDesc: 'Wrong answers reset progress. "Uncertain" reduces the streak by 1.',
      dualReadingTitle: 'Effective Learning',
      dualReadingDesc: 'Every word is shown with context and examples. This helps you learn German naturally and practically.',
    },
    legal: {
      impressum: 'Legal Notice',
      datenschutz: 'Privacy Policy',
      backToHome: 'Back to Home',
      allRightsReserved: 'All rights reserved.',
      impAngaben: 'Information according to § 5 TMG',
      impKontakt: 'Contact',
      impVerantwortlich: 'Responsible for content',
      impHaftung: 'Disclaimer',
      impHaftungText: 'The contents of this website have been created with the greatest care. However, we cannot guarantee the accuracy, completeness and timeliness of the content.',
      dsVerantwortlicher: 'Controller',
      dsDatenerfassung: 'Data collection on this website',
      dsDatenerfassungText: 'We only collect and store data that is necessary for providing our services. This includes email address and username during registration as well as learning progress data.',
      dsCookies: 'Cookies',
      dsCookiesText: 'This website uses technically necessary cookies for authentication and session management. No tracking cookies are used.',
      dsHosting: 'Hosting',
      dsHostingText: 'This website is hosted by Lovable/Supabase. The servers are located in the EU.',
      dsRechte: 'Your Rights',
      dsRechteText: 'You have the right to access, rectification, deletion and restriction of processing of your personal data. Contact us by email for this.',
      dsAenderungen: 'Changes',
      dsAenderungenText: 'We reserve the right to update this privacy policy to adapt it to changed legal situations or changes to our services.',
    },
    subscription: {
      premiumRequired: 'Premium Access Required',
      freeLessonsUsed: 'You have completed the first {count} free lessons.',
      youWantToAccess: 'You want to access:',
      lessonNumber: 'Lesson {number}',
      premiumBenefits: 'Premium Benefits',
      unlimitedLessons: 'Unlimited access to all lessons',
      allExercises: 'All exercise types available',
      progressTracking: 'Learning progress & statistics',
      month: 'month',
      cancelAnytime: 'Cancel anytime',
      subscribePremium: 'Subscribe Premium',
      maybeLater: 'Maybe later',
      manageSubscription: 'Manage Subscription',
      subscriptionActive: 'Premium Active',
      subscriptionCanceled: 'Premium Canceled',
      subscriptionEnds: 'Valid until {date}',
      subscriptionEndsAfterCancel: 'Still active until {date}',
      refreshStatus: 'Refresh Status',
    },
    admin: {
      flags: {
        title: 'Flagged Entries',
        description: 'Review and manage flagged vocabulary and texts',
        vocabularyFlags: 'Vocabulary Flags',
        dialogueFlags: 'Text Flags',
        pending: 'Pending',
        aiReviewed: 'AI Reviewed',
        approved: 'Approved',
        rejected: 'Rejected',
        resolved: 'Resolved',
        all: 'All',
        timestamp: 'Timestamp',
        role: 'Role',
        german: 'German',
        translation: 'Translation',
        list: 'List',
        listId: 'List ID',
        reason: 'Reason',
        status: 'Status',
        actions: 'Actions',
        approve: 'Approve',
        reject: 'Reject',
        delete: 'Delete',
        aiCheck: 'AI Check',
        aiRetry: 'AI Retry',
        tableView: 'Table',
        cardView: 'Cards',
        webResearch: 'Dictionary Research',
        webResearchHint: 'Enables Wiktionary search for more accurate results',
        bulkRetry: 'Check all pending',
        bulkRetryAll: 'Check all {count}',
        retryDialogTitle: 'AI Check with Hint',
        retryDialogDesc: 'Optionally add a hint for the AI.',
        adminHint: 'Admin hint (optional)',
        noFlags: 'No flags available',
        aiUsage: 'AI Usage',
        estimatedCost: 'Estimated Cost',
        reasonGermanWrong: 'German wrong',
        reasonVocalizationWrong: 'Vocalization wrong',
        reasonDuplicate: 'Duplicate',
        reasonTranslationWrong: 'Translation wrong',
        reasonGrammarWrong: 'Grammar error',
        reasonDeleteRequest: 'Delete requested',
        reasonUserReport: 'User report',
        reasonCaseWrong: 'Wrong case',
        reasonWaPrefix: 'Has وَ prefix',
        reasonBulkReview: 'Bulk review',
        reasonOther: 'Other',
        tooltipReject: 'Reject',
        tooltipDeleteVocab: 'Delete vocabulary',
        tooltipApprove: 'Approve',
        tooltipRetryAI: 'Retry AI check',
        tooltipDuplicateWarning: 'Duplicate: After correction the word {word} would match another entry in {book}',
        roleAdmin: 'Admin',
        roleTeacher: 'Teacher',
        roleUser: 'User',
      },
    },
    reportVocabulary: {
      title: 'Report Problem',
      selectReason: 'Select the reason for your report.',
      reasonTranslationWrong: 'Wrong Translation',
      reasonVocalizationWrong: 'Missing/Wrong Vocalization',
      reasonWaEnumeration: 'وَ-Enumeration',
      reasonGrammarWrong: 'Grammar Error',
      reasonOther: 'Other',
      describeProblem: 'Describe the problem...',
      notificationNote: 'You will be notified of the result after review.',
      reportButton: 'Report',
      reportTooltip: 'Report vocabulary',
      successTitle: 'Reported',
      successDescription: 'Thank you for your report! We\'ll review the vocabulary and inform you of the result.',
      errorDescription: 'The report could not be saved.',
    },
    myReports: {
      title: 'My Reports',
      description: 'Here you can see the status of your reported vocabulary and texts.',
      deleteAll: 'Delete all',
      deleteAllTitle: 'Delete all reports?',
      deleteAllConfirm: 'Do you really want to delete all {count} reports? This action cannot be undone.',
      deleting: 'Deleting...',
      typeVocabulary: 'Vocabulary',
      typeDialogue: 'Text',
      yourReport: 'Your report:',
      adminResponse: 'Response:',
      statusPendingMessage: 'Your report is being reviewed. You will receive a notification once it has been processed.',
      statusApprovedVocab: 'The vocabulary has been adjusted. Thank you for your help!',
      statusApprovedDialogue: 'The text has been adjusted. Thank you for your help!',
      statusRejectedMessage: 'The report was reviewed, but no error was found.',
      statusPending: 'Under Review',
      statusApproved: 'Approved',
      statusResolved: 'Corrected',
      statusRejected: 'Rejected',
      changesMade: 'Changes',
      before: 'Before',
      after: 'After',
      feedback: 'Response',
      viewVocabulary: 'View vocabulary',
      correctedOn: 'Corrected on',
      noChanges: 'No changes made',
    },
    streak: {
      celebration: {
        title: 'Streak achieved!',
        daysInRow: 'days in a row!',
        keepGoing: 'Keep it up!',
      },
    },
    contact: {
      title: 'Contact & Support',
      subtitle: 'We\'re happy to help with any questions or issues.',
      formTitle: 'Send a Message',
      formDescription: 'Describe your concern and we\'ll get back to you as soon as possible.',
      fields: {
        name: 'Name',
        email: 'Email',
        category: 'Category',
        message: 'Message',
      },
      placeholders: {
        name: 'Your name',
        email: 'your@email.com',
        category: 'Select a category',
        message: 'Describe your concern here...',
      },
      categories: {
        technical: 'Technical Issue',
        vocabulary: 'Question about Vocabulary',
        payment: 'Payment & Subscription',
        feedback: 'Feedback & Suggestions',
        other: 'Other',
      },
      buttons: {
        submit: 'Send Message',
        sending: 'Sending...',
      },
      success: {
        title: 'Message Sent!',
        description: 'Thank you for your message. We\'ll get back to you as soon as possible.',
        sendAnother: 'Send Another Message',
        backHome: 'Back to Home',
      },
      errors: {
        nameRequired: 'Please enter your name.',
        emailInvalid: 'Please enter a valid email address.',
        categoryRequired: 'Please select a category.',
        messageMinLength: 'Message must be at least 20 characters.',
        submitFailed: 'Could not send the message. Please try again.',
      },
      charMinimum: 'characters minimum',
      responseTime: 'We typically respond within 24-48 hours.',
    },
    adminSupport: {
      title: 'Support Requests',
      description: 'Manage all incoming support requests',
      filters: 'Filters',
      searchPlaceholder: 'Search by name, email or message...',
      filterByStatus: 'Filter by status',
      allRequests: 'All Requests',
      requests: 'Requests',
      requestsCount: 'requests found',
      noRequests: 'No requests available',
      total: 'Total',
      date: 'Date',
      status: 'Status',
      actions: 'Actions',
      view: 'View',
      requestDetails: 'Request Details',
      statusOpen: 'Open',
      statusInProgress: 'In Progress',
      statusResolved: 'Resolved',
      statusUpdated: 'Status has been updated',
      requestDeleted: 'Request has been deleted',
    },
    onboarding: {
      skipIntro: 'Skip intro',
      swipeHint: 'Swipe to navigate',
      tapToReveal: 'Tap to reveal',
      translateThis: 'Translate:',
      listenAndChoose: 'Listen and choose the correct letter',
      slide1: {
        title: 'Learn Authentic German.',
        subtitle: 'Different exercise types depending on the lesson.',
      },
      slide4: {
        ready: 'Ready to learn?',
        freeHint: '1-2 lessons unlimited. No signup required.',
        startButton: 'Start for free',
        loginLink: 'Already a member? Sign in',
      },
      community: {
        title: 'With the Community',
        subtitle: 'Together we make the app better.',
        flagHint: 'Report error',
        fixedErrors: 'Errors fixed',
        responseTime: 'Response time',
        thanks: 'Thanks to everyone who helps!',
      },
      feedback: {
        title: 'Your Ideas Matter',
        subtitle: 'Share feedback – we implement it.',
        supportButton: 'Help & Feedback',
        supportDesc: 'Questions, requests, improvements',
        recentIdeas: 'Implemented user ideas:',
        idea1: 'More listening exercises',
        idea2: 'Word pairs game',
        idea3: 'Offline mode',
      },
      personalized: {
        title: 'Your Start',
        subtitle: 'We adapt the learning path to you.',
        question: 'How well do you know German?',
        beginner: 'Complete beginner',
        beginnerDesc: 'I\'ll start with the basics.',
        advanced: 'Know the alphabet',
        advancedDesc: 'I can read, need vocabulary.',
        hint: 'You can change this later',
      },
      exercises: {
        letters: 'Letters',
        letterForms: 'Letter Forms',
        flashcards: 'Flashcards',
        multipleChoice: 'Multiple Choice',
        typing: 'Typing',
        wordPairs: 'Word Pairs',
        texts: 'Texts',
        listening: 'Listening',
        numbersIntro: 'Learn Numbers',
        numbersListen: 'Numbers Quiz',
        uhrzeitIntro: 'Learn Time',
        uhrzeitListen: 'Time Quiz',
      },
      exerciseDesc: {
        letters: 'Learn German grammar basics.',
        letterForms: 'Recognize the correct article (der, die, das).',
        flashcards: 'Classic flashcard learning with spaced repetition.',
        multipleChoice: 'Choose the correct translation from four options.',
        typing: 'Type German words with the keyboard.',
        wordPairs: 'Match German words with their Turkish translations.',
        texts: 'Practice with real dialogues and fill in the gaps.',
        listening: 'Train your ear for German pronunciation.',
        numbersIntro: 'Learn German numbers 1-10 with audio and examples.',
        numbersListen: 'Recognize German numbers by listening – quiz mode.',
        uhrzeitIntro: 'Learn German times with analog clocks.',
        uhrzeitListen: 'Recognize German times by listening – quiz mode.',
      },
      errorHints: {
        shadda: 'Pay attention to the doubled consonant.',
        sukun: 'No vowel here – the letter has Sukun.',
        harakat: 'Listen carefully to the short vowel.',
        tanwin: 'Listen for the -n ending (nunation).',
        madd: 'Pay attention to the long vowel.',
      },
    },
  },
  tr: {
    common: {
      loading: 'Yükleniyor...',
      save: 'Kaydet',
      saved: 'Kaydedildi',
      cancel: 'İptal',
      delete: 'Sil',
      edit: 'Düzenle',
      add: 'Ekle',
      search: 'Ara',
      back: 'Geri',
      close: 'Kapat',
      confirm: 'Onayla',
      yes: 'Evet',
      no: 'Hayır',
      optional: 'isteğe bağlı',
      error: 'Hata',
      welcome: 'Hoş Geldiniz',
      whatsNew: 'Yeni',
      updateAvailable: 'Yeni Sürüm Mevcut',
      updateDescription: 'GermanPath için yeni bir sürüm mevcut.',
      updateNow: 'Şimdi Güncelle',
      syncRecommended: 'Güncellemeden sonra verilerinizi senkronize etmenizi öneririz.',
      play: 'Oynat',
    },
    nav: {
      home: 'Ana Sayfa',
      books: 'Kitaplar',
      learn: 'Öğren',
      signIn: 'Giriş Yap',
      signOut: 'Çıkış Yap',
      admin: 'Yönetici',
      profile: 'Profil',
      support: 'Yardım',
    },
    profile: {
      description: 'Hesabını ve öğrenme ilerlemeni yönet',
      accountInfo: 'Hesap Bilgileri',
      dangerZone: 'Tehlike Bölgesi',
      resetDescription: 'Bu işlemler geri alınamaz.',
      resetProgress: 'Öğrenme İlerlemesini Sıfırla',
      resetConfirmTitle: 'İlerleme sıfırlansın mı?',
      resetConfirmDescription: 'Bu, tüm kelimeler için öğrenme ilerlemeni silecek. Sıfırdan başlayacaksın. Bu işlem geri alınamaz.',
      confirmReset: 'Evet, her şeyi sıfırla',
      changePassword: 'Şifre Değiştir',
      changePasswordDesc: 'Mevcut şifreni değiştirmek için yeni bir şifre gir.',
      newPassword: 'Yeni Şifre',
      confirmPassword: 'Şifreyi Onayla',
      changePasswordButton: 'Şifre Değiştir',
      passwordMinLength: 'Şifre en az 6 karakter olmalıdır.',
      passwordMismatch: 'Şifreler eşleşmiyor.',
      passwordChanged: 'Şifren değiştirildi.',
      version: 'Sürüm',
      buildDate: 'Yapım Tarihi',
      learningSettings: 'Öğrenme Ayarları',
      learningSettingsDesc: 'Öğrenme deneyimini özelleştir',
      unvocalizedDisplay: 'Basit Gösterim',
      unvocalizedDisplayDesc: 'Kelimeleri basit formatta göster',
      accountSecurity: 'Hesap & Güvenlik',
      accountSecurityDesc: 'Hesap verilerini ve şifreyi yönet',
      restoreProgress: 'İlerlemeyi Geri Yükle',
      restoreProgressDesc: '{count} kayıtlı ilerleme versiyonun var.',
      words: 'kelime',
      daysRemaining: 'gün kaldı',
      restore: 'Geri Yükle',
      restoreConfirmTitle: 'İlerleme geri yüklensin mi?',
      restoreConfirmDesc: 'Mevcut öğrenme ilerlemen {date} tarihindeki versiyonla değiştirilecek.',
      whatsNew: 'Yenilikler',
      whatsNewDesc: 'Son güncellemeler ve iyileştirmeler',
      showAll: 'Tümünü göster',
      dangerZoneDesc: 'Tüm öğrenme ilerlemeni sıfırla. Otomatik olarak bir yedek oluşturulur.',
      backupCreated: 'Otomatik olarak bir yedek oluşturulur.',
      record: 'Rekor',
      today: 'Bugün',
      dailyGoal: 'Günlük Hedef',
      showActivityHistory: 'Aktivite geçmişini göster',
      manage: 'Yönet',
      upgrade: 'Yükselt',
      validUntil: 'kadar',
      refreshApp: 'Uygulamayı yenile',
      refreshAppDesc: 'Önbelleği temizler ve en son sürümü yükler.',
      refreshAppButton: 'Şimdi yenile',
      refreshAppSuccess: 'Uygulama yeniden yükleniyor...',
    },
    auth: {
      signIn: 'Giriş Yap',
      signUp: 'Kayıt Ol',
      email: 'E-posta',
      password: 'Şifre',
      username: 'Kullanıcı Adı',
      usernameHint: 'Boş bırakılırsa otomatik oluşturulur. Daha sonra değiştirilemez.',
      optional: 'isteğe bağlı',
      forgotPassword: 'Şifreni mi unuttun?',
      noAccount: 'Hesabın yok mu?',
      hasAccount: 'Zaten hesabın var mı?',
      signInButton: 'Giriş Yap',
      signUpButton: 'Kayıt Ol',
      continueWithGoogle: 'Google ile devam et',
      orContinueWithEmail: 'Veya e-posta ile devam et',
      createAccount: 'Hesap Oluştur',
      welcomeBack: 'Tekrar hoş geldin',
      startJourney: 'Almanca kelime yolculuğuna başla',
      continueJourney: 'Almanca öğrenimine devam et',
      resetPassword: 'Şifre Sıfırla',
      resetPasswordDesc: 'E-posta adresini gir ve sana sıfırlama linki gönderelim.',
      sendResetLink: 'Link Gönder',
      resetLinkSent: 'Link Gönderildi!',
      resetLinkSentDesc: 'Şifre sıfırlama linki için e-postanı kontrol et.',
      backToLogin: 'Girişe Dön',
      confirmEmailTitle: 'E-postanı onayla',
      confirmEmailDesc: 'Sana onay linki içeren bir e-posta gönderdik. Hesabını aktif etmek için lütfen linke tıkla.',
      linkExpiredTitle: 'Link süresi doldu',
      linkExpiredDesc: 'Onay linki geçersiz veya süresi dolmuş. Lütfen tekrar dene.',
      tryAgain: 'Tekrar kayıt ol',
      usernameTaken: 'Bu kullanıcı adı zaten alınmış',
      generateNewUsername: 'Yeni isim oluştur',
      trySuggestions: 'Öneriler',
      usernameHelp: '3-15 karakter, sadece küçük harf ve rakam',
      passwordLeaked: 'Güvensiz şifre',
      passwordLeakedDesc: 'Bu şifre bir veri ihlalinde bulundu. Lütfen farklı bir şifre seç.',
      checkingPassword: 'Şifre kontrol ediliyor...',
    },
    welcome: {
      title: 'Hoş geldin!',
      subtitle: 'Aramıza katıldığın için mutluyuz. Almanca yolculuğun şimdi başlıyor.',
      yourUsername: 'Kullanıcı adın',
      usernameNote: 'Bu ismi profilinde göreceksin.',
      tip1: 'Kursundaki kelime listeleriyle başla',
      tip2: 'En iyi sonuçlar için her gün çalış',
      cta: 'Haydi başlayalım!',
    },
    lists: {
      title: 'Kelime Listelerim',
      subtitle: 'Öğrenmeye başlamak için bir liste seç',
      newList: 'Yeni Liste',
      createFirst: 'İlk Listeyi Oluştur',
      noLists: 'Henüz liste yok.',
      noBook: 'Diğer Listeler',
      words: 'kelime',
      learned: 'öğrenildi',
      deleteConfirm: 'Liste silinsin mi?',
      deleteWarning: 'Bu işlem geri alınamaz. Tüm kelimeler silinecek.',
      lesson: 'Ders',
      lessons: 'Dersler',
      editBookCover: 'Kitap Kapağını Düzenle',
      listName: 'Liste Kimliği',
      listNamePlaceholder: 'örn. Kitap I-1-10',
      book: 'Kitap',
      bookPlaceholder: 'örn. ABY I-1',
      lessonTitle: 'Ders',
      lessonTitlePlaceholder: 'örn. 1',
    },
    listDetail: {
      searchPlaceholder: 'Kelime ara...',
      addWord: 'Ekle',
      importWords: 'İçe Aktar',
      exportWords: 'Dışa Aktar',
      learnButton: 'Öğren',
      noWords: 'Henüz kelime yok',
      addFirstWord: 'İlk kelimeni ekle.',
      deleteWordConfirm: 'Kelime silinsin mi?',
      deleteWordWarning: 'Bu işlem geri alınamaz.',
      coverImageUpdated: 'Kapak resmi güncellendi',
      nameUpdated: 'İsim güncellendi',
      wordDeleted: 'Kelime silindi',
      allVocabDeleted: 'Tüm kelimeler silindi',
      allVocabDeletedDesc: '{count} kelime kaldırıldı. Yedek indirildi.',
      vocabRestored: 'Kelimeler geri yüklendi',
      vocabRestoredDesc: '{count} kelime geri yüklendi.',
      exportComplete: 'Dışa aktarma tamamlandı',
      exportCompleteDesc: '{count} kelime indirildi.',
      nothingToExport: 'Dışa aktarılacak bir şey yok',
      nothingToExportDesc: 'Önce kelime ekle.',
      sessionExpired: 'Lütfen tekrar giriş yapın',
      sessionExpiredDesc: 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.',
      aiCheckStarted: 'AI kontrolü başlatıldı',
      aiCheckStartedDesc: 'Kelimeler kontrol ediliyor.',
      undo: 'Geri Al',
      sortCreated: 'Oluşturulma tarihi',
      sortSource: 'Kaynak (A-Z)',
      sortGerman: 'Almanca (A-Z)',
      sortEnglish: 'İngilizce (A-Z)',
      sortTurkish: 'Türkçe (A-Z)',
      linkedItemsInfo: '{count} kelime diğer listelere bağlı',
      linkedItemsInfoDesc: 'Bu kelimeler korunacak ve otomatik olarak başka bir listeye atanacak.',
      linkableTooltip: 'Başka bir listede zaten mevcut ve kopyalanmak yerine bağlanacak.',
    },
    vocabForm: {
      addTitle: 'Yeni Kelime Ekle',
      editTitle: 'Kelimeyi Düzenle',
      sourceWord: 'Kaynak kelime',
      targetWordDE: 'Çeviri (Almanca)',
      targetWordEN: 'Çeviri (İngilizce)',
      targetWordTR: 'Çeviri (Türkçe)',
      example: 'Örnek cümle',
      note: 'Not',
      wordAdded: 'Kelime eklendi',
      wordUpdated: 'Kelime güncellendi',
      missingFields: 'Lütfen tüm zorunlu alanları doldurun.',
      duplicateError: 'Kopya bulundu',
      duplicateDescription: 'Bu kelime veritabanında zaten mevcut.',
      duplicateDescriptionWithList: 'Bu kelime zaten {listName} listesinde mevcut.',
      invalidSourceError: 'Geçersiz Almanca kelime',
      invalidSourceDescription: 'Kaynak kelime geçerli bir Almanca kelime olmalıdır.',
      autoTranslating: 'Çeviriler yükleniyor...',
      autoTranslateError: 'Otomatik çeviri başarısız oldu',
    },
    learn: {
      title: 'Öğrenmeye Başla',
      subtitle: 'Bir liste ve öğrenme modu seç.',
      selectList: 'Liste seç',
      selectMode: 'Mod seç',
      sessionSize: 'Oturum boyutu',
      sessionSizeDesc: 'Oturum başına kelime',
      flashcards: 'Kartlar',
      multipleChoice: 'Çoktan Seçmeli',
      flashcardsDesc: 'Klasik kart öğrenme',
      multipleChoiceDesc: 'Doğru cevabı seç',
      startLearning: 'Öğrenmeye Başla',
      noLists: 'Liste bulunamadı',
      createListFirst: 'Başlamak için bir kitap seç.',
      minWordsRequired: 'Min. 4 kelime gerekli',
      dueWords: 'bekleyen',
      noDueHint: 'Kelimeler pekişiyor – yakında tekrar çalış!',
      selectListFirst: 'Yukarıdan ders seç',
      chooseModeFirst: 'Bir mod seç',
      wordsCount: 'kelime',
      currentlyLearning: 'Şu An Öğreniliyor',
      recommendations: 'Öneriler',
      noCurrentLists: 'Henüz bir liste başlatmadın',
      step1SelectList: 'Adım 1: Aşağıdan bir liste seç',
      step2SelectMode: 'Şimdi öğrenme modunu seç ve oturum boyutunu ayarla',
      step3Ready: 'Öğrenmeye hazır!',
      listSelected: 'seçildi',
      allDueSelected: 'Tüm bekleyen kelimeler seçildi',
      allDueWords: 'Tüm bekleyen kelimeler',
      typingMode: 'Yazma',
      wordPairs: 'Kelime Eşleri',
      dialogueMode: 'Metinler',
      crossListLearning: 'Listeler arası öğren',
      selectLesson: 'Ders seç',
      chooseFromList: 'Listelerinden seç',
      progressMastered: 'öğrenildi',
      progressLearning: 'devam ediyor',
      progressReviewing: 'tekrar',
      progressNew: 'yeni',
      // Temel açıklamalar
      lettersDescription: 'Arap alfabesini okumayı öğren: formlar, noktalar ve bağlantılar.',
      lettersCompletedHint: '✔️ Alfabe tamamlandı – şimdi okumaya geçelim.',
      harakatDescription: 'Kelimeleri doğru okuyabilmen için hareke işaretlerini öğren.',
      harakatHint: 'Harekesiz okumak başlangıçta çok zordur.',
      formsDescription: 'Harflerin kelime içinde nasıl göründüğünü pratik et (başta · ortada · sonda).',
      formsHint: 'Bu adım okumayı çok daha kolay hale getirir.',
      firstWordsDescription: 'Harekeli basit gerçek kelimeleri oku.',
      firstWordsRecommendation: 'Form eğitiminden sonra önerilir.',
      dueWordsDescription: 'Tüm derslerden kelimeleri tekrar et.',
      dueWordsHint: 'Sonrası için – önce temelleri güçlendir.',
      selectLessonDescription: 'Ne pratik yapmak istediğini seç.',
      tanwinDescription: 'Belirsiz formlar için tenvini (an/in/un) öğren.',
      tanwinHint: 'Tenvin kelime sonunda duyulur – gramer için önemli.',
      sukunDescription: 'Sesli harf olmayan heceleri tanı (durma işareti).',
      sukunHint: 'Sükun: sadece sessiz harf söylenir.',
      shaddaDescription: 'Çift sessiz harfleri tanı (şedde).',
      shaddaHint: 'Şedde = harf daha uzun/çift söylenir.',
      nowActive: 'Şu an aktif',
      letterIntro: {
        skip: 'Geç',
        back: 'Geri',
        next: 'İleri',
        startNow: 'Şimdi pratik yap!',
        slide1Title: 'Almanca farklı yazılır!',
        slide1Desc: 'Almanca\'da özel harfler (ä, ö, ü, ß) ve farklı yazım kuralları vardır.',
        germanLabel: 'Türkçe:',
        sourceLabel: 'Almanca:',
        leftToRight: 'soldan → sağa',
        rightToLeft: 'sağdan ← sola',
        exampleHouse: '💡 "Ev" kelimesi böyle okunur:',
        slide2Title: 'Artikelleri anlama',
        slide2Desc: 'Almanca\'da her ismin bir cinsiyeti vardır: der (eril), die (dişil), das (nötr).',
        exampleWord: 'Örnek kelime:',
        meansHouse: '("ev" anlamına gelir)',
        baseForm: 'Temel form',
        atEnd: 'Sonda',
        inMiddle: 'Ortada',
        atStart: 'Başta',
        endForm: 'Son form',
        middleForm: 'Orta form',
        initialForm: 'Başlangıç formu',
        connectionTip: '💡 İpucu: Bağlantı çizgilerine dikkat et – harflerin birbirine nasıl bağlandığını gösteriyorlar!',
        slide3Title: 'Hazırsın!',
        slide3Desc: 'Artık Almanca\'nın nasıl çalıştığını biliyorsun. Hadi pratik yapalım!',
        youCanDoIt: 'Yapabilirsin! 🎉',
        whatYouLearned: 'Öğrendiklerin:',
        learned1: '✓ Almanca soldan sağa okunur',
        learned2: '✓ Her ismin bir artikeli vardır',
        learned3: '✓ Özel harfler önemlidir (ä, ö, ü, ß)',
      },
    },
    flashcard: {
      tapToReveal: 'Görmek için dokun',
      translation: 'Çeviri',
      known: 'Biliyorum',
      uncertain: 'Emin Değilim',
      unknown: 'Bilmiyorum',
      sessionComplete: 'Tebrikler!',
      correctAnswers: 'Doğru cevaplar',
      backToLearn: 'Öğrenmeye Dön',
      continueSession: 'Devam Et',
      wordsReviewed: '{count} kelimeyi tekrarladın.',
      backToList: 'Listeye Dön',
      backToDashboard: 'Panele Dön',
      learnMore: 'Daha Fazla Öğren',
    },
    multipleChoice: {
      selectAnswer: 'Doğru cevabı seç',
      correct: 'Doğru!',
      incorrect: 'Yanlış',
      next: 'İleri',
    },
    typing: {
      typeGermanWord: 'Almanca kelimeyi yaz',
      check: 'Kontrol Et',
      dontKnow: 'Bilmiyorum',
      correctAnswer: 'Doğru cevap',
    },
    orientation: {
      landscapeHint: '💡 İpucu: Daha iyi bir görünüm için cihazınızı dikey moda çevirin',
    },
    import: {
      title: 'Kelimeleri İçe Aktar',
      pasteOrUpload: 'Metin yapıştır veya dosya yükle',
      uploadFile: 'Dosya yükle',
      preview: 'Önizleme',
      importButton: 'İçe Aktar',
      columnMapping: 'Sütun eşleme',
      source: 'Kaynak',
      target: 'Çeviri',
      newItems: 'Yeni',
      duplicates: 'Tekrar',
      invalid: 'Geçersiz',
    },
    status: {
      new: 'Yeni',
      learning: 'Öğreniliyor',
      reviewing: 'Tekrar',
      mastered: 'Ustalaşıldı',
    },
    language: {
      selectLanguage: 'Dil',
      german: 'Deutsch',
      english: 'English',
      turkish: 'Türkçe',
    },
    landing: {
      hero: {
        tagline: 'Almanca kelime bilgisine giden yapılandırılmış bir yol.',
        textbookSeries: '(Türkçe konuşanlar için Almanca kursları)',
        startLearning: 'Öğrenmeye başla',
        createFreeAccount: 'Ücretsiz hesap oluştur',
        scrollHint: 'Daha fazla bilgi',
      },
      about: {
        title: 'Neden GermanPath?',
        description1: 'GermanPath, Almanca kelime dağarcığını sistematik olarak oluşturmanıza yardımcı olur – entegrasyon kursları, dil sertifikaları veya kişisel kelime listeniz için.',
        description2: 'Kelime listelerimiz özellikle Türkçe konuşan Almanca öğrenenlerinin ihtiyaçlarına göre tasarlanmıştır.',
        description3: 'Aralıklı tekrar ile daha etkili öğrenin ve kelimeleri uzun vadeli aklınızda tutun.',
      },
      howItWorks: {
        title: 'Nasıl Çalışır',
        step1Title: 'Bir kurs seç',
        step1Desc: 'Yapılandırılmış Almanca kurs serisinden seç.',
        step2Title: 'Almanca kelime öğren',
        step2Desc: 'Kelimeleri örnekler ve notlarla bağlamında öğren.',
        step3Title: 'Aralıklı tekrarla gözden geçir',
        step3Desc: 'Bilimsel olarak kanıtlanmış aralıklarla kelimeleri aklında tut.',
      },
      books: {
        title: 'Mevcut Kurslar',
        subtitle: 'Önizleme mevcut. Kişisel ilerleme kayıt sonrası kaydedilir.',
        levelBeginner: 'Başlangıç (A1)',
        levelElementary: 'Temel (A2)',
        levelIntermediate: 'Orta (B1)',
        levelAdvanced: 'İleri (B2+)',
        vocabCount: 'Kelime',
      },
      disclaimer: {
        title: 'Yasal Uyarı:',
        text1: 'GermanPath, Almanca öğrenimini desteklemek için bağımsız bir öğrenme platformudur.',
        text2: 'Platform, resmi dil enstitüleriyle bağlantılı değildir. Tüm ticari markalar ve isimler sahiplerine aittir.',
        text3: 'GermanPath resmi kurs materyallerini sağlamaz. Harici bağlantılar yalnızca bilgilendirme amaçlıdır.',
        googleAuth: 'Bu uygulama oturum açma için Google kimlik doğrulaması kullanır. "Google ile devam et" seçeneğini kullanarak, hesap oluşturma için temel profil bilgilerinizin (ad, e-posta) işlenmesini kabul edersiniz.',
      },
      footer: {
        tagline: 'Almanca kelime bilgisine giden yapılandırılmış bir yol',
        rights: 'Tüm hakları saklıdır.',
      },
      features: {
        title: 'Çeşitli Alıştırma Türleri',
        subtitle: 'Almanca öğrenmenize yardımcı olacak farklı öğrenme yöntemlerini keşfedin',
      },
    },
    theme: {
      light: 'Açık',
      dark: 'Koyu',
      system: 'Sistem',
    },
    dashboard: {
      todayProgress: 'Bugün',
      wordsToStreak: 'Seri için {count} kelime daha',
      xpExplanation: '+10 XP doğru, +5 XP belirsiz, +2 XP yanlış',
      longestStreak: 'En uzun seri',
      activityHeatmap: 'Aktivite geçmişi',
      weeklyOverview: 'Bu hafta',
      noActivity: 'Aktivite yok',
      streakHint: 'Seri için min. 15 kelime',
      bonusTitle: '+20 XP Bonus!',
      bonusDescription: 'Bugün 30 kelime - Çalışkanlık bonusu!',
      weekdayMon: 'Pzt',
      weekdayWed: 'Çar',
      weekdayFri: 'Cum',
    },
    dialogue: {
      noGaps: 'Bu diyalogda pratik yapılacak boşluk yok.',
    },
    readingTyping: {
      notQuiteRight: 'Tam doğru değil',
      perfectWithHarakat: 'Mükemmel!',
      correct: 'Doğru!',
      typeGermanWord: 'Almanca kelimeyi yaz:',
      hideTranslation: 'Çeviriyi gizle',
      showTranslation: 'Çeviriyi göster',
      correctAnswer: 'Doğru cevap:',
    },
    session: {
      sessionProgress: 'Oturum {current}/{total}',
    },
    listForm: {
      missingName: 'İsim eksik',
      missingNameDesc: 'Lütfen bir liste adı girin.',
      missingBook: 'Kitap eksik',
      missingBookDesc: 'Lütfen bir kitap seçin veya girin.',
      lessonTitleLabel: 'Ders başlığı',
      placeholderDe: 'örn. Tanışma',
      placeholderEn: 'örn. Tanışma',
      placeholderTr: 'örn. Tanışma',
      noBookFound: 'Kitap bulunamadı',
    },
    progress: {
      title: 'İlerlemeniz',
      totalWords: 'Toplam kelime',
      mastered: 'Ustalaşılan',
      learning: 'Öğrenilen',
      dueToday: 'Bugün tekrar',
      streak: 'Gün serisi',
      noProgress: 'İlerlemenizi görmek için öğrenmeye başlayın.',
      startLearning: 'Şimdi başla',
      listBreakdown: 'Derslere göre dağılım',
    },
    statusExplanations: {
      total: { label: 'Toplam', description: 'Bu listedeki toplam kelime sayısı' },
      due: { label: 'Bekleyen', description: 'Tekrar tarihi gelmiş ve bugün çalışılması gereken kelimeler' },
      new: { label: 'Yeni', description: 'Hiç çalışılmamış kelimeler' },
      learning: { label: 'Öğreniliyor', description: '1 kez doğru cevaplanan kelimeler (3 gün sonra tekrar)' },
      reviewing: { label: 'Tekrar', description: '2-3 kez doğru cevaplanan kelimeler (7-14 gün sonra tekrar)' },
      mastered: { label: 'Ustalaşıldı', description: '4+ kez doğru cevaplanan kelimeler (30+ gün sonra tekrar)' },
    },
    spacedRepetition: {
      title: 'Öğrenme sistemi nasıl çalışır?',
      description: 'Bilimsel olarak kanıtlanmış aralıklı tekrar yöntemini kullanıyoruz.',
      howItWorks: 'Bir kelimeyi ne kadar sık doğru cevaplarsanız, o kadar az sorulur:',
      newDesc: 'Yeni: Hiç çalışılmadı',
      learningDesc: 'Öğreniliyor: 1x doğru → 3 gün sonra tekrar',
      reviewingDesc: 'Tekrar: 2-3x doğru → 7-14 gün ara',
      masteredDesc: 'Ustalaşıldı: 4+ kez doğru → 30+ gün ara',
      intervals: 'Tekrar aralıkları',
      intervalsDesc: 'Yanlış cevaplar ilerlemeyi sıfırlar. "Emin Değilim" seriyi 1 azaltır.',
      dualReadingTitle: 'Etkili Öğrenme',
      dualReadingDesc: 'Her kelime bağlam ve örneklerle gösterilir. Bu sayede Almanca\'yı doğal ve pratik bir şekilde öğrenirsiniz.',
    },
    legal: {
      impressum: 'Yasal Bildirim',
      datenschutz: 'Gizlilik Politikası',
      backToHome: 'Ana Sayfaya Dön',
      allRightsReserved: 'Tüm hakları saklıdır.',
      impAngaben: '§ 5 TMG\'ye göre bilgiler',
      impKontakt: 'İletişim',
      impVerantwortlich: 'İçerikten sorumlu',
      impHaftung: 'Sorumluluk Reddi',
      impHaftungText: 'Bu web sitesinin içerikleri büyük özenle oluşturulmuştur. Ancak içeriklerin doğruluğu, eksiksizliği ve güncelliği için garanti veremeyiz.',
      dsVerantwortlicher: 'Sorumlu Kişi',
      dsDatenerfassung: 'Bu web sitesinde veri toplama',
      dsDatenerfassungText: 'Yalnızca hizmetlerimizi sağlamak için gerekli verileri topluyor ve saklıyoruz. Bu, kayıt sırasında e-posta adresi ve kullanıcı adının yanı sıra öğrenme ilerleme verilerini içerir.',
      dsCookies: 'Çerezler',
      dsCookiesText: 'Bu web sitesi kimlik doğrulama ve oturum yönetimi için teknik olarak gerekli çerezler kullanır. İzleme çerezleri kullanılmamaktadır.',
      dsHosting: 'Barındırma',
      dsHostingText: 'Bu web sitesi Lovable/Supabase tarafından barındırılmaktadır. Sunucular AB\'de bulunmaktadır.',
      dsRechte: 'Haklarınız',
      dsRechteText: 'Kişisel verilerinize erişim, düzeltme, silme ve işlemenin kısıtlanması hakkına sahipsiniz. Bunun için bize e-posta ile ulaşın.',
      dsAenderungen: 'Değişiklikler',
      dsAenderungenText: 'Değişen yasal durumlar veya hizmetlerimizdeki değişikliklere uyum sağlamak için bu gizlilik politikasını güncelleme hakkını saklı tutuyoruz.',
    },
    subscription: {
      premiumRequired: 'Premium Erişim Gerekli',
      freeLessonsUsed: 'İlk {count} ücretsiz dersi tamamladın.',
      youWantToAccess: 'Erişmek istediğin:',
      lessonNumber: 'Ders {number}',
      premiumBenefits: 'Premium Avantajları',
      unlimitedLessons: 'Tüm derslere sınırsız erişim',
      allExercises: 'Tüm alıştırma türleri',
      progressTracking: 'Öğrenme ilerlemesi & istatistikler',
      month: 'ay',
      cancelAnytime: 'İstediğin zaman iptal',
      subscribePremium: 'Premium Abone Ol',
      maybeLater: 'Belki daha sonra',
      manageSubscription: 'Aboneliği Yönet',
      subscriptionActive: 'Premium Aktif',
      subscriptionCanceled: 'Premium İptal Edildi',
      subscriptionEnds: '{date} tarihine kadar geçerli',
      subscriptionEndsAfterCancel: '{date} tarihine kadar hâlâ aktif',
      refreshStatus: 'Durumu Güncelle',
    },
    admin: {
      flags: {
        title: 'Bildirilen Girişler',
        description: 'Bildirilen kelime ve metinleri incele ve yönet',
        vocabularyFlags: 'Kelime Bildirimleri',
        dialogueFlags: 'Metin Bildirimleri',
        pending: 'Beklemede',
        aiReviewed: 'YZ İncelendi',
        approved: 'Onaylandı',
        rejected: 'Reddedildi',
        resolved: 'Çözüldü',
        all: 'Tümü',
        timestamp: 'Zaman',
        role: 'Rol',
        german: 'Almanca',
        translation: 'Çeviri',
        list: 'Liste',
        listId: 'Liste ID',
        reason: 'Sebep',
        status: 'Durum',
        actions: 'İşlemler',
        approve: 'Onayla',
        reject: 'Reddet',
        delete: 'Sil',
        aiCheck: 'YZ Kontrol',
        aiRetry: 'YZ Tekrar',
        tableView: 'Tablo',
        cardView: 'Kartlar',
        webResearch: 'Sözlük Araştırması',
        webResearchHint: 'Daha doğru sonuçlar için Wiktionary araması etkinleştirir',
        bulkRetry: 'Tüm bekleyenleri kontrol et',
        bulkRetryAll: 'Tümünü kontrol et ({count})',
        retryDialogTitle: 'İpucu ile YZ Kontrolü',
        retryDialogDesc: 'İsteğe bağlı olarak YZ için bir ipucu ekleyebilirsin.',
        adminHint: 'Yönetici ipucu (isteğe bağlı)',
        noFlags: 'Bildirim yok',
        aiUsage: 'YZ Kullanımı',
        estimatedCost: 'Tahmini Maliyet',
        reasonGermanWrong: 'Almanca yanlış',
        reasonVocalizationWrong: 'Hareke yanlış',
        reasonDuplicate: 'Tekrar',
        reasonTranslationWrong: 'Çeviri yanlış',
        reasonGrammarWrong: 'Dilbilgisi hatası',
        reasonDeleteRequest: 'Silme istendi',
        reasonUserReport: 'Kullanıcı bildirimi',
        reasonCaseWrong: 'Yanlış hal eki',
        reasonWaPrefix: 'وَ öneki var',
        reasonBulkReview: 'Toplu inceleme',
        reasonOther: 'Diğer',
        tooltipReject: 'Reddet',
        tooltipDeleteVocab: 'Kelimeyi sil',
        tooltipApprove: 'Onayla',
        tooltipRetryAI: 'Yapay zeka kontrolü',
        tooltipDuplicateWarning: 'Kopya: Düzeltmeden sonra {word} kelimesi {book} içindeki başka bir girişle eşleşecektir',
        roleAdmin: 'Yönetici',
        roleTeacher: 'Öğretmen',
        roleUser: 'Kullanıcı',
      },
    },
    reportVocabulary: {
      title: 'Problem bildir',
      selectReason: 'Bildirimin nedenini seç.',
      reasonTranslationWrong: 'Yanlış çeviri',
      reasonVocalizationWrong: 'Eksik/Yanlış harekeler',
      reasonWaEnumeration: 'وَ-Sıralama',
      reasonGrammarWrong: 'Dilbilgisi hatası',
      reasonOther: 'Diğer',
      describeProblem: 'Problemi açıkla...',
      notificationNote: 'İnceleme sonrasında sonuç hakkında bilgilendirileceksin.',
      reportButton: 'Bildir',
      reportTooltip: 'Kelimeyi bildir',
      successTitle: 'Bildirildi',
      successDescription: 'Bildirimin için teşekkürler! Kelimeyi inceleyip sonucu bildireceğiz.',
      errorDescription: 'Bildirim kaydedilemedi.',
    },
    myReports: {
      title: 'Bildirilerim',
      description: 'Bildirdiğin kelime ve metinlerin durumunu burada görebilirsin.',
      deleteAll: 'Hepsini sil',
      deleteAllTitle: 'Tüm bildirimleri sil?',
      deleteAllConfirm: '{count} bildirimi silmek istediğinden emin misin? Bu işlem geri alınamaz.',
      deleting: 'Siliniyor...',
      typeVocabulary: 'Kelime',
      typeDialogue: 'Metin',
      yourReport: 'Bildirimin:',
      adminResponse: 'Yanıt:',
      statusPendingMessage: 'Bildirimin inceleniyor. İşlendiğinde bildirim alacaksın.',
      statusApprovedVocab: 'Kelime düzenlendi. Yardımın için teşekkürler!',
      statusApprovedDialogue: 'Metin düzenlendi. Yardımın için teşekkürler!',
      statusRejectedMessage: 'Bildirim incelendi ancak hata bulunamadı.',
      statusPending: 'İnceleniyor',
      statusApproved: 'Onaylandı',
      statusResolved: 'Düzeltildi',
      statusRejected: 'Reddedildi',
      changesMade: 'Değişiklikler',
      before: 'Önce',
      after: 'Sonra',
      feedback: 'Yanıt',
      viewVocabulary: 'Kelimeyi görüntüle',
      correctedOn: 'Düzeltildiği tarih',
      noChanges: 'Değişiklik yapılmadı',
    },
    streak: {
      celebration: {
        title: 'Seri başarıldı!',
        daysInRow: 'gün üst üste!',
        keepGoing: 'Devam et!',
      },
    },
    contact: {
      title: 'İletişim & Destek',
      subtitle: 'Sorularınız veya sorunlarınız için yardımcı olmaktan mutluluk duyarız.',
      formTitle: 'Mesaj Gönder',
      formDescription: 'Konunuzu açıklayın, en kısa sürede size döneceğiz.',
      fields: {
        name: 'İsim',
        email: 'E-posta',
        category: 'Kategori',
        message: 'Mesaj',
      },
      placeholders: {
        name: 'Adınız',
        email: 'email@adresiniz.com',
        category: 'Bir kategori seçin',
        message: 'Konunuzu buraya açıklayın...',
      },
      categories: {
        technical: 'Teknik Sorun',
        vocabulary: 'Kelime Hakkında Soru',
        payment: 'Ödeme & Abonelik',
        feedback: 'Geri Bildirim & Öneriler',
        other: 'Diğer',
      },
      buttons: {
        submit: 'Mesaj Gönder',
        sending: 'Gönderiliyor...',
      },
      success: {
        title: 'Mesaj Gönderildi!',
        description: 'Mesajınız için teşekkürler. En kısa sürede size döneceğiz.',
        sendAnother: 'Başka Mesaj Gönder',
        backHome: 'Ana Sayfaya Dön',
      },
      errors: {
        nameRequired: 'Lütfen adınızı girin.',
        emailInvalid: 'Lütfen geçerli bir e-posta adresi girin.',
        categoryRequired: 'Lütfen bir kategori seçin.',
        messageMinLength: 'Mesaj en az 20 karakter olmalıdır.',
        submitFailed: 'Mesaj gönderilemedi. Lütfen tekrar deneyin.',
      },
      charMinimum: 'karakter minimum',
      responseTime: 'Genellikle 24-48 saat içinde yanıt veriyoruz.',
    },
    adminSupport: {
      title: 'Destek Talepleri',
      description: 'Tüm gelen destek taleplerini yönet',
      filters: 'Filtreler',
      searchPlaceholder: 'İsim, e-posta veya mesaj ara...',
      filterByStatus: 'Duruma göre filtrele',
      allRequests: 'Tüm Talepler',
      requests: 'Talepler',
      requestsCount: 'talep bulundu',
      noRequests: 'Talep bulunamadı',
      total: 'Toplam',
      date: 'Tarih',
      status: 'Durum',
      actions: 'İşlemler',
      view: 'Görüntüle',
      requestDetails: 'Talep Detayları',
      statusOpen: 'Açık',
      statusInProgress: 'İşlemde',
      statusResolved: 'Çözüldü',
      statusUpdated: 'Durum güncellendi',
      requestDeleted: 'Talep silindi',
    },
    onboarding: {
      skipIntro: 'Atla',
      swipeHint: 'Gezinmek için kaydır',
      tapToReveal: 'Görmek için dokun',
      translateThis: 'Çevir:',
      listenAndChoose: 'Dinle ve doğru harfi seç',
      slide1: {
        title: 'Otantik Almanca öğren.',
        subtitle: 'Derse göre farklı alıştırma türleri.',
      },
      slide4: {
        ready: 'Öğrenmeye hazır mısın?',
        freeHint: '1-2 ders sınırsız. Kayıt gerektirmez.',
        startButton: 'Ücretsiz başla',
        loginLink: 'Zaten üye misin? Giriş yap',
      },
      community: {
        title: 'Toplulukla Birlikte',
        subtitle: 'Birlikte uygulamayı daha iyi yapıyoruz.',
        flagHint: 'Hata bildir',
        fixedErrors: 'Düzeltilen hatalar',
        responseTime: 'Yanıt süresi',
        thanks: 'Yardım eden herkese teşekkürler!',
      },
      feedback: {
        title: 'Fikirleriniz Önemli',
        subtitle: 'Geri bildirim paylaşın – uyguluyoruz.',
        supportButton: 'Yardım & Geri Bildirim',
        supportDesc: 'Sorular, istekler, iyileştirmeler',
        recentIdeas: 'Uygulanan kullanıcı fikirleri:',
        idea1: 'Daha fazla dinleme egzersizi',
        idea2: 'Kelime eşleştirme oyunu',
        idea3: 'Çevrimdışı mod',
      },
      personalized: {
        title: 'Senin Başlangıcın',
        subtitle: 'Öğrenme yolunu sana göre uyarlıyoruz.',
        question: 'Almanca seviyeniz nedir?',
        beginner: 'Tamamen yeni',
        beginnerDesc: 'Temellerden başlayacağım.',
        advanced: 'Temeller biliniyor',
        advancedDesc: 'Biraz okuyabiliyorum, kelime lazım.',
        hint: 'Bunu daha sonra değiştirebilirsin',
      },
      exercises: {
        letters: 'Dilbilgisi',
        letterForms: 'Artikeller',
        flashcards: 'Kartlar',
        multipleChoice: 'Çoktan Seçmeli',
        typing: 'Yazma',
        wordPairs: 'Kelime Eşleştirme',
        texts: 'Metinler',
        listening: 'Dinleme',
        numbersIntro: 'Sayıları Öğren',
        numbersListen: 'Sayı Quizi',
        uhrzeitIntro: 'Saati Öğren',
        uhrzeitListen: 'Saat Quizi',
      },
      exerciseDesc: {
        letters: 'Almanca dilbilgisi temellerini öğren.',
        letterForms: 'Doğru artikeli tanı (der, die, das).',
        flashcards: 'Aralıklı tekrar ile klasik kart öğrenimi.',
        multipleChoice: 'Dört seçenek arasından doğru çeviriyi seç.',
        typing: 'Klavye ile Almanca kelimeler yaz.',
        wordPairs: 'Almanca kelimeleri Türkçe çevirileriyle eşleştir.',
        texts: 'Gerçek diyaloglarla pratik yap ve boşlukları doldur.',
        listening: 'Almanca telaffuz için kulağını eğit.',
        numbersIntro: 'Almanca 1-10 sayılarını ses ve örneklerle öğren.',
        numbersListen: 'Dinleyerek Almanca sayıları tanı – quiz modu.',
        uhrzeitIntro: 'Analog saatlerle Almanca saatleri öğren.',
        uhrzeitListen: 'Dinleyerek Almanca saatleri tanı – quiz modu.',
      },
      errorHints: {
        shadda: 'Çift sessiz harfe dikkat et.',
        sukun: 'Burada sesli harf yok – harfte Sükun var.',
        harakat: 'Kısa sesli harfi dikkatli dinle.',
        tanwin: '-n son ekini dinle (tenvin).',
        madd: 'Uzun sesli harfe dikkat et.',
      },
    },
  },
};
